import * as THREE from '@phdsoft/three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { Geometry } from 'three/examples/jsm/deprecated/Geometry.js';
import { Util } from '@phdsoft/common';
import { Injectable } from '@angular/core';
import { Vector3 } from '@phdsoft/three';
import { DigitalTwinService } from 'src/app/digital-twin/digital-twin.service';
import { Asset } from '../busines-data/Framework/Asset';
import { Section } from '../busines-data/Framework/Section';
import { StructuralElement } from '../busines-data/SectionElements/StructureElements/StructuralElement';
import { Box3 } from 'three';
import { BehaviorSubject } from 'rxjs';
import { AssetInfoViewService } from 'src/app/template/asset-info-view/asset-info-view/asset-info-view.service';
import { AssetManager } from 'src/app/asset-manager/asset-manager';
import { Region } from '../busines-data/Framework/Region';
import { NotificationService } from 'src/app/signalr/notification.service';
import { WorkableElement } from '../busines-data/SectionElements/WorkableElement';
import { GraphicsMaterials } from 'src/app/digital-twin/graphics/graphics-materials';

class IStiffeners {
    Guid: string
    SurfaceDesign: ISurfaceDesign
    properties: IProperty
    section: ISection
}
class IBrackets {
    Guid: string
    SurfaceDesign: ISurfaceDesign
    properties: IProperty
    section: ISection
}

type SerializedBox3 = {
    min: { x: number; y: number; z: number }
    max: { x: number; y: number; z: number }
}

class ISection {
    Guid: String
}
class ISurfaceDesign {
    name: string
    geometry: IGeometry
}
class IGeometry {
    boundingBox: Box3 | SerializedBox3
    attributes: IAttributes
    index: { array: ArrayLike<number> }
}

class IAttributes {
    position: { array: ArrayLike<number> }
}
class IProperty {
    Geometry: IPropertyGeometry
    General: IGeneral
}
class IGeneral {
    Label: string
}
class IPropertyGeometry {
    WebThickness: Number
    FlangeThickness: Number
}


@Injectable({ providedIn: 'root' })
export class LinesComputerHelper {


    private isRunningUtilities = new BehaviorSubject<boolean>(false);
    castIsRunningUtilities = this.isRunningUtilities.asObservable();
    private bBox: THREE.Box3;
    private regionCenterBox: THREE.Vector3;

    private plates = []
    private stiffeners = []
    private facePlates = []
    private brackets = []
    private currentRegionId: string;
    private minimalStiffenerArea = 0.01

    private sectionGeometry = []

    private webs = []
    private flanges = []

    private linesToUpdate: UpdateLineGeometryViewModel;
    private refelinesToUpdate: AddLineGeometryViewModel;

    constructor(
        private digitalTwinService: DigitalTwinService,
        private assetInfoService: AssetInfoViewService,
        private assetManager: AssetManager,
        private notificationService: NotificationService
    ) {
        this.plates = []
        this.stiffeners = []
        this.facePlates = []
        this.brackets = []
        this.webs = []
        this.flanges = []
        this.linesToUpdate = new UpdateLineGeometryViewModel();
        this.refelinesToUpdate = new AddLineGeometryViewModel();
        this.linesToUpdate.AssetId = this.assetManager.asset?.assetInfo?.AssetId;
        this.refelinesToUpdate.AssetId = this.assetManager.asset?.assetInfo?.AssetId;
        this.linesToUpdate.Lines = []
        this.refelinesToUpdate.Lines = []
        this.regionCenterBox = new THREE.Vector3();
    }

    setRunningUtilities(bool: boolean) {
        this.isRunningUtilities.next(bool)
    }

    private resetData() {
        this.plates = []
        this.stiffeners = []
        this.facePlates = []
        this.brackets = []
        this.webs = []
        this.flanges = []
        this.linesToUpdate = new UpdateLineGeometryViewModel();
        this.refelinesToUpdate = new AddLineGeometryViewModel();
        this.linesToUpdate.AssetId = this.assetManager.asset?.assetInfo?.AssetId;
        this.refelinesToUpdate.AssetId = this.assetManager.asset?.assetInfo?.AssetId;
        this.linesToUpdate.Lines = []
        this.refelinesToUpdate.Lines = []
        this.regionCenterBox = new THREE.Vector3();
    }

    private resetStructuralData() {
        this.plates = []
        this.stiffeners = []
        this.facePlates = []
        this.brackets = []
        this.webs = []
        this.flanges = []
    }

    public async executeForAllRegions(asset: Asset) {
        this.setRunningUtilities(true);
        let _currentRegions = asset.GetStructureVisibleRegions();
        this.assetInfoService.setRegionAndSections(asset.GetVisibleRegions());
        for (let i = 0; i < _currentRegions.length; i++) {
            await this.assetManager.loadProperties([_currentRegions[i].Guid], true);

            await this.executeReferenceLinesForRegion(asset, _currentRegions[i]);

            this.executeFacePlatesForRegion(asset, _currentRegions[i]);
            this.executeBracketsForRegion(asset, _currentRegions[i]);
            this.executeStiffenersForRegion(asset, _currentRegions[i]);

            this.linesToUpdate.AssetId = asset.assetInfo.AssetId;
            this.linesToUpdate.ChangeWebToFlange = false;
            await this.digitalTwinService.updateLinesGeometry(this.linesToUpdate).then(() => {
                console.log("Update lines for " + _currentRegions[i].Name);
            });

        }

        // temporary solution to notify user when utility ends running
        await this.waitUntilUtilityEndsExecution(_currentRegions.length);
        const utilityEndsNotification = {
            dateTime: new Date().toLocaleTimeString('pt-BR'),
            fileUrl: null,
            message: "Success",
            title: "Finished utility execution",
        };
        this.notificationService.addNotificationModel(utilityEndsNotification)
        this.setRunningUtilities(false);
        // console.log("Finished updating lines for selected regions");
    }

    public executeStiffenersForRegion(asset: Asset, region: Region) {
        this.resetStructuralData();
        this.bBox = new THREE.Box3();
        this.linesToUpdate.RegionId = region.Guid;
        this.refelinesToUpdate.RegionId = region.Guid;
        this.setRegionBoundingBoxAndCenter(region);
        region.sections.forEach(section => {
            if (!section) {
                return;
            }

            this.setCurrentData(section, true);
            this.findFlangesForStiffeners(this.stiffeners, this.plates, true, 0.05);

            const foundInfo = this.stiffeners.find(x => !Util.isNullOrUndefined(x.properties?.General?.CompleteStiffenerId));
            if (foundInfo) {
                for (const iterator of this.stiffeners) {
                    const property = iterator.properties;
                    if (!Util.isNullOrUndefined(property)) {
                        if (property.Geometry.FlangeThickness && property.Geometry.FlangeThickness > 0) {
                            iterator.SurfaceDesign.userData.isFlange = true;
                            const webs = this.stiffeners.filter(x => x.properties.General.CompleteStiffenerId === property.General.CompleteStiffenerId
                                && (!x.properties.Geometry.FlangeThickness || x.properties.Geometry.FlangeThickness === 0));
                            if (webs) {
                                for (const web of webs) {
                                    if (!web.SurfaceDesign.userData.Flanges)
                                        web.SurfaceDesign.userData.Flanges = [];
                                    web.SurfaceDesign.userData.Flanges.push(iterator.Guid);
                                }
                            }
                        }
                    }
                }

                for (const iterator of this.stiffeners) {
                    const property = iterator.properties;
                    if (!Util.isNullOrUndefined(property)) {
                        if (property.Geometry.FlangeThickness && property.Geometry.FlangeThickness > 0) {

                        }
                        else {
                            this.webs.push(iterator);
                            iterator.SurfaceDesign.userData.isFlange = false;
                        }
                    }
                }

                // this.matchFlangesWithWebs(this.stiffeners);
                this.computeOnFrontSide(section.sectionNormal);
            }
            else {
                for (const iterator of this.stiffeners) {

                    const property = iterator.properties;
                    if (!Util.isNullOrUndefined(property)) {
                        if (property.Geometry.FlangeThickness && property.Geometry.FlangeThickness > 0) {
                            // this.flanges.push(iterator)
                            iterator.SurfaceDesign.userData.isFlange = true;
                        }
                        else {
                            this.webs.push(iterator);
                            iterator.SurfaceDesign.userData.isFlange = false;
                            iterator.SurfaceDesign.userData.Flanges = [];
                        }
                    }
                }

                this.matchStiffenerFlangesWithWebs(this.stiffeners, true, section.sectionNormal);
                this.computeOnFrontSide(section.sectionNormal);
            }
        })
        // this.linesToUpdate.AssetId = asset.assetInfo.AssetId;
        // this.digitalTwinService.updateLinesGeometry(this.linesToUpdate).then(() => {
        //     // console.log('Finished updating 2d stiffener lines')
        //     // this.snackBar.open('Finished updating 2d stiffener lines', '', {
        //     //     horizontalPosition: 'right',
        //     //     verticalPosition: 'bottom',
        //     //     duration: 3000
        //     // });
        //     console.log("Finished updating stiffeners for " + region.Name);
        // });
    }

    public executeStiffeners(asset: Asset, changeWebToFlange: boolean) {
        this.setRunningUtilities(true)
        this.resetData();
        this.bBox = new THREE.Box3();
        if (asset.mode === 'REGIONS' || asset.mode === 'SECTIONS') {
            let _currentRegions = asset.GetStructureVisibleRegions();
            if (_currentRegions) {
                const _currentRegion = _currentRegions[0];
                this.linesToUpdate.RegionId = _currentRegion.Guid;
                this.refelinesToUpdate.RegionId = _currentRegion.Guid;
                this.setRegionBoundingBoxAndCenter(_currentRegion);
                for (let i = 0; i < _currentRegion.sections.length; i++) {
                    const _section = _currentRegion.sections[i];
                    if (_section.visible) {
                        this.setCurrentData(_section, true);
                        this.findFlangesForStiffeners(this.stiffeners, this.plates, true, 0.05);

                        const foundInfo = this.stiffeners.find(x => !Util.isNullOrUndefined(x.properties?.General?.CompleteStiffenerId));
                        if (foundInfo) {

                            for (const iterator of this.stiffeners) {
                                const property = iterator.properties;
                                if (!Util.isNullOrUndefined(property)) {
                                    if (property.Geometry.FlangeThickness && property.Geometry.FlangeThickness > 0) {
                                        iterator.SurfaceDesign.userData.isFlange = true;
                                        const webs = this.stiffeners.filter(x => x.properties.General.CompleteStiffenerId === property.General.CompleteStiffenerId
                                            && (!x.properties.Geometry.FlangeThickness || x.properties.Geometry.FlangeThickness === 0));
                                        if (webs) {
                                            for (const web of webs) {
                                                if (!web.SurfaceDesign.userData.Flanges)
                                                    web.SurfaceDesign.userData.Flanges = [];
                                                web.SurfaceDesign.userData.Flanges.push(iterator.Guid);
                                            }
                                        }
                                    }
                                }
                            }

                            for (const iterator of this.stiffeners) {
                                const property = iterator.properties;
                                if (!Util.isNullOrUndefined(property)) {
                                    if (property.Geometry.FlangeThickness && property.Geometry.FlangeThickness > 0) {

                                    }
                                    else {
                                        this.webs.push(iterator);
                                        iterator.SurfaceDesign.userData.isFlange = false;
                                    }
                                }
                            }

                            // this.matchFlangesWithWebs(this.stiffeners);
                            this.computeOnFrontSide(_section.sectionNormal);
                        }
                        else {
                            for (const iterator of this.stiffeners) {

                                const property = iterator.properties;
                                if (!Util.isNullOrUndefined(property)) {
                                    if ((property.Geometry.FlangeThickness && property.Geometry.FlangeThickness > 0)
                                        ||
                                        (iterator.SurfaceDesign.userData.sendLine === null ||
                                            iterator.SurfaceDesign.userData.sendLine === undefined ||
                                            iterator.SurfaceDesign.userData.sendLine.length === 0)
                                    ) {
                                        // this.flanges.push(iterator)
                                        iterator.SurfaceDesign.userData.isFlange = true;
                                    }
                                    else {
                                        this.webs.push(iterator);
                                        iterator.SurfaceDesign.userData.isFlange = false;
                                        if(!iterator.SurfaceDesign.userData.Flanges)
                                            iterator.SurfaceDesign.userData.Flanges = [];
                                    }
                                }
                            }

                            this.matchStiffenerFlangesWithWebs(this.stiffeners, true, _section.sectionNormal);
                            this.computeOnFrontSide(_section.sectionNormal);
                        }
                    }
                }
                this.linesToUpdate.AssetId = asset.assetInfo.AssetId;
                this.linesToUpdate.ChangeWebToFlange = changeWebToFlange;
                this.digitalTwinService.updateLinesGeometry(this.linesToUpdate).then(() => {
                    //console.log('Finished updating 2d stiffener lines')
                    // this.snackBar.open('Finished updating 2d stiffener lines', '', {
                    //     horizontalPosition: 'right',
                    //     verticalPosition: 'bottom',
                    //     duration: 3000
                    // });
                });
            }
        }
        this.setRunningUtilities(false)
    }

    public executeStiffenersLongitudinals(asset: Asset, changeWebToFlange: boolean) {
        this.setRunningUtilities(true)
        this.resetData();
        this.bBox = new THREE.Box3();
        if (asset.mode === 'REGIONS' || asset.mode === 'SECTIONS') {
            let _currentRegions = asset.GetStructureVisibleRegions();
            if (_currentRegions) {
                const _currentRegion = _currentRegions[0];
                this.linesToUpdate.RegionId = _currentRegion.Guid;
                this.refelinesToUpdate.RegionId = _currentRegion.Guid;
                this.setRegionBoundingBoxAndCenter(_currentRegion);
                for (let i = 0; i < _currentRegion.sections.length; i++) {
                    const _section = _currentRegion.sections[i];
                    if (_section.visible) {
                        this.setCurrentData(_section, true);
                        this.findFlangesForStiffeners(this.stiffeners, this.plates, true, 0.05);

                        const foundInfo = this.stiffeners.find(x => !Util.isNullOrUndefined(x.properties?.General?.CompleteStiffenerId));
                        if (foundInfo) {

                            for (const iterator of this.stiffeners) {
                                const property = iterator.properties;
                                if (!Util.isNullOrUndefined(property)) {
                                    if (property.Geometry.FlangeThickness && property.Geometry.FlangeThickness > 0) {
                                        iterator.SurfaceDesign.userData.isFlange = true;
                                        const webs = this.stiffeners.filter(x => x.properties.General.CompleteStiffenerId === property.General.CompleteStiffenerId
                                            && (!x.properties.Geometry.FlangeThickness || x.properties.Geometry.FlangeThickness === 0));
                                        if (webs) {
                                            for (const web of webs) {
                                                if (!web.SurfaceDesign.userData.Flanges)
                                                    web.SurfaceDesign.userData.Flanges = [];
                                                web.SurfaceDesign.userData.Flanges.push(iterator.Guid);
                                            }
                                        }
                                    }
                                }
                            }

                            for (const iterator of this.stiffeners) {
                                const property = iterator.properties;
                                if (!Util.isNullOrUndefined(property)) {
                                    if (property.Geometry.FlangeThickness && property.Geometry.FlangeThickness > 0) {

                                    }
                                    else {
                                        this.webs.push(iterator);
                                        iterator.SurfaceDesign.userData.isFlange = false;
                                    }
                                }
                            }

                            // this.matchFlangesWithWebs(this.stiffeners);
                            this.computeOnFrontSide(_section.sectionNormal);
                        }
                        else {
                            const outerStiffeners = []
                            for (const iterator of this.stiffeners) {

                                const property = iterator.properties;
                                if (!Util.isNullOrUndefined(property)) {
                                    if ((property.Geometry.FlangeThickness && property.Geometry.FlangeThickness > 0)
                                        &&
                                        (iterator.SurfaceDesign.userData.sendLine === null ||
                                            iterator.SurfaceDesign.userData.sendLine === undefined ||
                                            iterator.SurfaceDesign.userData.sendLine.length === 0)
                                    ) {
                                        // this.flanges.push(iterator)
                                        iterator.SurfaceDesign.userData.isFlange = true;
                                    }
                                    else if ((property.Geometry.WebThickness && property.Geometry.WebThickness > 0) &&
                                        (iterator.SurfaceDesign.userData.sendLine &&
                                            iterator.SurfaceDesign.userData.sendLine.length > 0)
                                    ) {
                                        this.webs.push(iterator);
                                        iterator.SurfaceDesign.userData.isFlange = false;
                                        iterator.SurfaceDesign.userData.Flanges = [];
                                    }
                                    else {
                                        iterator.SurfaceDesign.userData.isFlange = false;
                                        iterator.SurfaceDesign.userData.IsOuter = true;
                                        iterator.SurfaceDesign.userData.Flanges = [];
                                        outerStiffeners.push(iterator)
                                    }
                                }
                            }

                            this.findFlangesForStiffeners(outerStiffeners, this.webs, true, 0.15);

                            this.matchStiffenerFlangesWithWebs(this.stiffeners, true, _section.sectionNormal);

                            for (const iterator of outerStiffeners) {
                                this.webs.push(iterator);
                            }
                            this.computeOnFrontSide(_section.sectionNormal);
                        }
                    }
                }
                this.linesToUpdate.AssetId = asset.assetInfo.AssetId;
                this.linesToUpdate.ChangeWebToFlange = changeWebToFlange;
                this.digitalTwinService.updateLinesGeometry(this.linesToUpdate).then(() => {
                    //console.log('Finished updating 2d stiffener lines')
                    // this.snackBar.open('Finished updating 2d stiffener lines', '', {
                    //     horizontalPosition: 'right',
                    //     verticalPosition: 'bottom',
                    //     duration: 3000
                    // });
                });
            }
        }
        this.setRunningUtilities(false)
    }

    public executeBracketsForRegion(asset: Asset, region: Region) {
        this.resetStructuralData();
        this.bBox = new THREE.Box3();
        this.linesToUpdate.RegionId = region.Guid;
        this.refelinesToUpdate.RegionId = region.Guid;
        this.setRegionBoundingBoxAndCenter(region);
        region.sections.forEach(section => {
            if (!section) {
                return;
            }
            this.setCurrentData(section, true);
            this.findFlangesForStiffeners(this.brackets, this.plates, true, 0.09);

            const bracketsToTest = [];
            const foundInfo = this.brackets.find(x => !Util.isNullOrUndefined(x.properties?.General?.CompleteBracketId));
            if (foundInfo) {
                for (const bracket of this.brackets) {
                    const property = bracket.properties;
                    if (!Util.isNullOrUndefined(property)) {
                        if (property.Geometry.FlangeThickness && property.Geometry.FlangeThickness > 0) {
                            bracket.SurfaceDesign.userData.isFlange = true;
                            const web = this.brackets.find(x => x.properties.General.CompleteBracketId === property.General.CompleteBracketId && (!x.properties.Geometry.FlangeThickness || x.properties.Geometry.FlangeThickness === 0));
                            if (web) {
                                if (!web.SurfaceDesign.userData.Flanges)
                                    web.SurfaceDesign.userData.Flanges = [];
                                web.SurfaceDesign.userData.Flanges.push(bracket.Guid);
                            }
                        }
                        else {
                            this.webs.push(bracket);
                            bracket.SurfaceDesign.userData.isFlange = false;
                        }
                    }
                    if (!bracket.SurfaceDesign.userData.sendLine) {
                        bracketsToTest.push(bracket);
                    }
                }
                this.computeOnFrontSide(section.sectionNormal);
            }
            else {
                for (const bracket of this.brackets) {
                    const property = bracket.properties;
                    if (!Util.isNullOrUndefined(property)) {
                        if (property.General.CompleteBracketLabel) {

                        }

                        if (property.Geometry.FlangeThickness && property.Geometry.FlangeThickness > 0) {
                            bracket.SurfaceDesign.userData.isFlange = true;
                        }
                        else {
                            this.webs.push(bracket);
                            bracket.SurfaceDesign.userData.isFlange = false;
                        }
                    }
                    if (!bracket.SurfaceDesign.userData.sendLine) {
                        bracketsToTest.push(bracket);
                    }
                }
                const stiffenersFlanges = []
                const stiffeners = [];
                for (const iterator of this.stiffeners) {
                    const property = iterator.properties;
                    if (!Util.isNullOrUndefined(property)) {
                        if (property.Geometry.FlangeThickness && property.Geometry.FlangeThickness > 0) {
                            iterator.SurfaceDesign.userData.isFlange = true;
                            stiffenersFlanges.push(iterator)
                        }
                        else {
                            stiffeners.push(iterator);
                            iterator.SurfaceDesign.userData.isFlange = false;
                        }
                    }
                }

                this.findFlangesForStiffeners(bracketsToTest, stiffeners, false, 0.05);


                const elementsToCheck = this.checkElementPlateCollision(stiffeners);
                for (const iterator of elementsToCheck) {
                    stiffenersFlanges.push(iterator);
                }

                this.matchFlangesWithWebs(stiffenersFlanges, false, section.sectionNormal);
                this.computeOnFrontSide(section.sectionNormal);
            }

        })
        // this.linesToUpdate.AssetId = asset.assetInfo.AssetId;
        // this.digitalTwinService.updateLinesGeometry(this.linesToUpdate).then(() => {
        //     //console.log('Finished updating 2d brackets lines')
        //     // this.snackBar.open('Finished updating 2d brackets lines', '', {
        //     //     horizontalPosition: 'right',
        //     //     verticalPosition: 'bottom',
        //     //     duration: 3000
        //     // });
        //     console.log("Finished updating brackets for " + region.Name);
        // });
    }

    public executeBrackets(asset: Asset) {
        this.setRunningUtilities(true)
        this.resetData();
        this.bBox = new THREE.Box3();
        if (asset.mode === 'REGIONS' || asset.mode === 'SECTIONS') {
            let _currentRegions = asset.GetStructureVisibleRegions();
            if (_currentRegions) {
                const _currentRegion = _currentRegions[0];
                this.linesToUpdate.RegionId = _currentRegion.Guid;
                this.refelinesToUpdate.RegionId = _currentRegion.Guid;
                this.setRegionBoundingBoxAndCenter(_currentRegion);
                for (let i = 0; i < _currentRegion.sections.length; i++) {
                    const _section = _currentRegion.sections[i];
                    if (_section.visible) {
                        this.setCurrentData(_section, true);
                        this.findFlangesForStiffeners(this.brackets, this.plates, true, 0.09);

                        const bracketsToTest = [];
                        const foundInfo = this.brackets.find(x => !Util.isNullOrUndefined(x.properties?.General?.CompleteBracketId));
                        if (foundInfo) {
                            for (const bracket of this.brackets) {
                                const property = bracket.properties;
                                if (!Util.isNullOrUndefined(property)) {
                                    if (property.Geometry.FlangeThickness && property.Geometry.FlangeThickness > 0) {
                                        bracket.SurfaceDesign.userData.isFlange = true;
                                        const web = this.brackets.find(x => x.properties.General.CompleteBracketId === property.General.CompleteBracketId && (!x.properties.Geometry.FlangeThickness || x.properties.Geometry.FlangeThickness === 0));
                                        if (web) {
                                            if (!web.SurfaceDesign.userData.Flanges)
                                                web.SurfaceDesign.userData.Flanges = [];
                                            web.SurfaceDesign.userData.Flanges.push(bracket.Guid);
                                        }
                                    }
                                    else {
                                        this.webs.push(bracket);
                                        bracket.SurfaceDesign.userData.isFlange = false;
                                    }
                                }
                                if (!bracket.SurfaceDesign.userData.sendLine) {
                                    bracketsToTest.push(bracket);
                                }
                            }
                            this.computeOnFrontSide(_section.sectionNormal);
                        }
                        else {
                            for (const bracket of this.brackets) {
                                const property = bracket.properties;
                                if (!Util.isNullOrUndefined(property)) {
                                    if (property.General.CompleteBracketLabel) {

                                    }

                                    if (property.Geometry.FlangeThickness && property.Geometry.FlangeThickness > 0) {
                                        bracket.SurfaceDesign.userData.isFlange = true;
                                    }
                                    else {
                                        this.webs.push(bracket);
                                        bracket.SurfaceDesign.userData.isFlange = false;
                                    }
                                }
                                if (!bracket.SurfaceDesign.userData.sendLine) {
                                    bracketsToTest.push(bracket);
                                }
                            }
                            // const stiffenersFlanges = []
                            // const stiffeners = [];
                            // for (const iterator of this.stiffeners) {
                            //     const property = iterator.properties;
                            //     if (!Util.isNullOrUndefined(property)) {
                            //         if (property.Geometry.FlangeThickness && property.Geometry.FlangeThickness > 0) {
                            //             iterator.SurfaceDesign.userData.isFlange = true;
                            //             stiffenersFlanges.push(iterator)
                            //         }
                            //         else {
                            //             stiffeners.push(iterator);
                            //             iterator.SurfaceDesign.userData.isFlange = false;
                            //         }
                            //     }
                            // }

                            // this.findFlangesForStiffeners(bracketsToTest, stiffeners, true, 0.15);


                            // const elementsToCheck = this.checkElementPlateCollision(stiffeners);
                            // for (const iterator of elementsToCheck) {
                            //     stiffenersFlanges.push(iterator);
                            // }

                            // this.matchFlangesWithWebs(stiffenersFlanges, false, _section.sectionNormal);
                            this.computeOnFrontSide(_section.sectionNormal);
                        }

                    }
                }
                this.linesToUpdate.AssetId = asset.assetInfo.AssetId;
                this.linesToUpdate.ChangeWebToFlange = false;
                this.digitalTwinService.updateLinesGeometry(this.linesToUpdate).then(() => {
                    //console.log('Finished updating 2d brackets lines')
                    // this.snackBar.open('Finished updating 2d brackets lines', '', {
                    //     horizontalPosition: 'right',
                    //     verticalPosition: 'bottom',
                    //     duration: 3000
                    // });
                });
                // this.digitalTwinService.addLinesGeometry(this.refelinesToUpdate);
            }
        }
        this.setRunningUtilities(false)
    }

    public executeBracketsLikeStiffenersNew(asset: Asset, changeWebToFlange: boolean) {
        this.setRunningUtilities(true)
        this.resetData();
        this.bBox = new THREE.Box3();
        if (asset.mode === 'REGIONS' || asset.mode === 'SECTIONS') {
            let _currentRegions = asset.GetStructureVisibleRegions();
            if (_currentRegions) {
                const _currentRegion = _currentRegions[0];
                this.linesToUpdate.RegionId = _currentRegion.Guid;
                this.refelinesToUpdate.RegionId = _currentRegion.Guid;
                this.setRegionBoundingBoxAndCenter(_currentRegion);
                for (let i = 0; i < _currentRegion.sections.length; i++) {
                    const _section = _currentRegion.sections[i];
                    if (_section.visible) {
                        this.setCurrentData(_section, true);
                        this.findFlangesForStiffeners(this.brackets, this.plates, true, 0.05);

                        const foundInfo = this.brackets.find(x => !Util.isNullOrUndefined(x.properties?.General?.CompleteStiffenerId));
                        if (foundInfo) {

                            for (const iterator of this.brackets) {
                                const property = iterator.properties;
                                if (!Util.isNullOrUndefined(property)) {
                                    if (property.Geometry.FlangeThickness && property.Geometry.FlangeThickness > 0) {
                                        iterator.SurfaceDesign.userData.isFlange = true;
                                        const webs = this.brackets.filter(x => x.properties.General.CompleteStiffenerId === property.General.CompleteStiffenerId
                                            && (!x.properties.Geometry.FlangeThickness || x.properties.Geometry.FlangeThickness === 0));
                                        if (webs) {
                                            for (const web of webs) {
                                                if (!web.SurfaceDesign.userData.Flanges)
                                                    web.SurfaceDesign.userData.Flanges = [];
                                                web.SurfaceDesign.userData.Flanges.push(iterator.Guid);
                                            }
                                        }
                                    }
                                }
                            }

                            for (const iterator of this.brackets) {
                                const property = iterator.properties;
                                if (!Util.isNullOrUndefined(property)) {
                                    if (property.Geometry.FlangeThickness && property.Geometry.FlangeThickness > 0) {

                                    }
                                    else {
                                        this.webs.push(iterator);
                                        iterator.SurfaceDesign.userData.isFlange = false;
                                    }
                                }
                            }

                            // this.matchFlangesWithWebs(this.stiffeners);
                            this.computeOnFrontSide(_section.sectionNormal);
                        }
                        else {
                            for (const iterator of this.brackets) {

                                const property = iterator.properties;
                                if (!Util.isNullOrUndefined(property)) {
                                    if ((property.Geometry.FlangeThickness && property.Geometry.FlangeThickness > 0)
                                        ||
                                        (iterator.SurfaceDesign.userData.sendLine === null ||
                                            iterator.SurfaceDesign.userData.sendLine === undefined ||
                                            iterator.SurfaceDesign.userData.sendLine.length === 0)
                                    ) {
                                        // this.flanges.push(iterator)
                                        iterator.SurfaceDesign.userData.isFlange = true;
                                    }
                                    else {
                                        this.webs.push(iterator);
                                        iterator.SurfaceDesign.userData.isFlange = false;
                                        if(!iterator.SurfaceDesign.userData.Flanges)
                                            iterator.SurfaceDesign.userData.Flanges = [];
                                    }
                                }
                            }

                            this.matchStiffenerFlangesWithWebs(this.brackets, true, _section.sectionNormal);
                            this.matchStiffenerFlangesWithWebs(this.brackets, true, _section.sectionNormal);
                            this.computeOnFrontSide(_section.sectionNormal);
                        }
                    }
                }
                this.linesToUpdate.AssetId = asset.assetInfo.AssetId;
                this.linesToUpdate.ChangeWebToFlange = changeWebToFlange;
                this.digitalTwinService.updateLinesGeometry(this.linesToUpdate).then(() => {
                    //console.log('Finished updating 2d stiffener lines')
                    // this.snackBar.open('Finished updating 2d stiffener lines', '', {
                    //     horizontalPosition: 'right',
                    //     verticalPosition: 'bottom',
                    //     duration: 3000
                    // });
                });
            }
        }
        this.setRunningUtilities(false)
    }

    public executeBracketsFixBrackets(asset: Asset) {
        this.setRunningUtilities(true)
        this.resetData();
        this.bBox = new THREE.Box3();
        let _flanges = [];
        if (asset.mode === 'REGIONS' || asset.mode === 'SECTIONS') {
            let _currentRegions = asset.GetStructureVisibleRegions();
            if (_currentRegions) {
                const _currentRegion = _currentRegions[0];
                this.linesToUpdate.RegionId = _currentRegion.Guid;
                this.refelinesToUpdate.RegionId = _currentRegion.Guid;
                this.setRegionBoundingBoxAndCenter(_currentRegion);
                for (let i = 0; i < _currentRegion.sections.length; i++) {
                    const _section = _currentRegion.sections[i];
                    if (_section.visible) {
                        this.setCurrentData(_section, true);
                        this.findFlangesForStiffeners(this.brackets, this.plates, true, 0.09);
                        let flangesCandidates = this.findFlangesForStiffeners(this.stiffeners, this.plates, true, 0.1);

                        const bracketsToTest = [];
                        const foundInfo = this.brackets.find(x => !Util.isNullOrUndefined(x.properties?.General?.CompleteBracketId));
                        if (foundInfo) {
                            for (const bracket of this.brackets) {
                                const property = bracket.properties;
                                if (!Util.isNullOrUndefined(property)) {
                                    if (property.Geometry.FlangeThickness && property.Geometry.FlangeThickness > 0) {
                                        bracket.SurfaceDesign.userData.isFlange = true;
                                        const web = this.brackets.find(x => x.properties.General.CompleteBracketId === property.General.CompleteBracketId && (!x.properties.Geometry.FlangeThickness || x.properties.Geometry.FlangeThickness === 0));
                                        if (web) {
                                            if (!web.SurfaceDesign.userData.Flanges)
                                                web.SurfaceDesign.userData.Flanges = [];
                                            web.SurfaceDesign.userData.Flanges.push(bracket.Guid);
                                        }
                                    }
                                    else {
                                        this.webs.push(bracket);
                                        bracket.SurfaceDesign.userData.isFlange = false;
                                    }
                                }
                                if (!bracket.SurfaceDesign.userData.sendLine) {
                                    bracketsToTest.push(bracket);
                                }
                            }
                            this.computeOnFrontSide(_section.sectionNormal);
                        }
                        else {
                            for (const bracket of this.brackets) {
                                const property = bracket.properties;
                                if (!Util.isNullOrUndefined(property)) {
                                    if (property.General.CompleteBracketLabel) {

                                    }

                                    if (property.Geometry.FlangeThickness && property.Geometry.FlangeThickness > 0) {
                                        bracket.SurfaceDesign.userData.isFlange = true;
                                    }
                                    else {
                                        this.webs.push(bracket);
                                        bracket.SurfaceDesign.userData.isFlange = false;
                                    }
                                }
                                if (!bracket.SurfaceDesign.userData.sendLine) {
                                    bracketsToTest.push(bracket);
                                }
                            }
                            const stiffenersFlanges = []
                            const stiffeners = [];
                            for (const iterator of this.stiffeners) {
                                const property = iterator.properties;
                                if (!Util.isNullOrUndefined(property)) {
                                    if (property.Geometry.FlangeThickness && property.Geometry.FlangeThickness > 0) {
                                        iterator.SurfaceDesign.userData.isFlange = true;
                                        stiffenersFlanges.push(iterator)
                                    }
                                    else {
                                        stiffeners.push(iterator);
                                        iterator.SurfaceDesign.userData.isFlange = false;
                                    }
                                }
                            }

                            this.findFlangesForStiffeners(bracketsToTest, stiffeners, true, 0.15);



                            let st = this.matchStiffenerFlangesWithWebsTest(flangesCandidates, this.stiffeners, true, _section.sectionNormal);

                            const elementsToCheck = this.checkElementPlateCollision(stiffeners);
                            for (const iterator of elementsToCheck) {
                                stiffenersFlanges.push(iterator);
                            }

                            let fl = this.matchFlangesWithWebs(st, false, _section.sectionNormal);

                            // this.linesToUpdate.Lines = [];



                            for (const iterator of fl) {
                                (iterator as StructuralElement).currentMaterial = GraphicsMaterials.redMaterial;
                                (iterator as StructuralElement).currentRenderMaterial = GraphicsMaterials.redMaterial;
                                // (iterator as StructuralElement).defaultMaterial = GraphicsMaterials.redMaterial;

                                _flanges.push({ Item1: iterator.Guid, Item2: null, Item3: null, Item4: null, Item5: false });

                            }



                            // this.computeOnFrontSide(_section.sectionNormal);
                        }

                    }
                }

                debugger
                this.linesToUpdate.Lines = _flanges
                this.linesToUpdate.AssetId = asset.assetInfo.AssetId;
                this.linesToUpdate.ChangeWebToFlange = false;
                this.linesToUpdate.ChangeStiffenerToBracket = true;
                this.digitalTwinService.updateLinesGeometry(this.linesToUpdate).then(() => {
                    //console.log('Finished updating 2d brackets lines')
                    // this.snackBar.open('Finished updating 2d brackets lines', '', {
                    //     horizontalPosition: 'right',
                    //     verticalPosition: 'bottom',
                    //     duration: 3000
                    // });
                });
                // this.digitalTwinService.addLinesGeometry(this.refelinesToUpdate);
            }
        }
        this.setRunningUtilities(false)
    }

    private checkElementPlateCollision(elements: any[]): any[] {
        const notCollided = [];
        for (const element of elements) {

            if (!this.checkIntersectionWithPlates(this.plates, element, 0.01, false)) {
                element.SurfaceDesign.userData.isFlange = true;
                notCollided.push(element);
            }
        }
        return notCollided;
    }

    public getElementBorder(element: StructuralElement): Array<any> {
        let borderElement: THREE.LineSegments = null
        if (element.SurfaceDesign.children.length > 0) {
            borderElement = element.SurfaceDesign.children[0] as THREE.LineSegments;
            // borderElement.geometry.
        }
        return null;
    }

    public executeFacePlatesForRegion(asset: Asset, region: Region) {
        this.resetStructuralData();
        this.bBox = new THREE.Box3();
        this.linesToUpdate.RegionId = region.Guid;
        this.refelinesToUpdate.RegionId = region.Guid;
        this.setRegionBoundingBoxAndCenter(region);
        region.sections.forEach(section => {
            if (!section) {
                return;
            }
            this.setCurrentData(section, true);
            // this.mergeSectionPlates(_section.name, _section.id);
            // this.findFlangesForStiffeners();
            this.findFacePlatesIntersectionWithPlates();
        })
        // this.linesToUpdate.AssetId = asset.assetInfo.AssetId;
        // this.digitalTwinService.updateLinesGeometry(this.linesToUpdate).then(() => {
        //     //console.log('Finished updating 2d faceplates lines')
        //     // this.snackBar.open('Finished updating 2d faceplates lines', '', {
        //     //     horizontalPosition: 'right',
        //     //     verticalPosition: 'bottom',
        //     //     duration: 3000
        //     // });
        // });
    }

    public executeFacePlates(asset: Asset) {
        this.setRunningUtilities(true)
        this.resetData();
        this.bBox = new THREE.Box3();
        if (asset.mode === 'REGIONS' || asset.mode === 'SECTIONS') {
            let _currentRegions = asset.GetStructureVisibleRegions();
            if (_currentRegions) {
                const _currentRegion = _currentRegions[0];
                this.linesToUpdate.RegionId = _currentRegion.Guid;
                this.refelinesToUpdate.RegionId = _currentRegion.Guid;
                this.setRegionBoundingBoxAndCenter(_currentRegion);
                for (let i = 0; i < _currentRegion.sections.length; i++) {
                    const _section = _currentRegion.sections[i];
                    if (_section.visible) {
                        this.setCurrentData(_section, true);
                        // this.mergeSectionPlates(_section.name, _section.id);
                        // this.findFlangesForStiffeners();
                        this.findFacePlatesIntersectionWithPlates();
                    }
                }
                this.linesToUpdate.AssetId = asset.assetInfo.AssetId;
                this.linesToUpdate.ChangeWebToFlange = false;
                this.digitalTwinService.updateLinesGeometry(this.linesToUpdate).then(() => {
                    //console.log('Finished updating 2d faceplates lines')
                    // this.snackBar.open('Finished updating 2d faceplates lines', '', {
                    //     horizontalPosition: 'right',
                    //     verticalPosition: 'bottom',
                    //     duration: 3000
                    // });
                });
                // this.digitalTwinService.addLinesGeometry(this.refelinesToUpdate);
            }
        }
        this.setRunningUtilities(false)
    }

    public async executeReferenceLinesForRegion(asset: Asset, region: Region) {
        this.resetData();
        this.bBox = new THREE.Box3();
        this.linesToUpdate.RegionId = region.Guid;
        this.refelinesToUpdate.RegionId = region.Guid;
        this.currentRegionId = region.Guid;
        this.setRegionBoundingBoxAndCenter(region);

        for (let i = 0; i < region.sections.length; i++) {
            const _section = region.sections[i];
            this.setCurrentData(_section, true);
            this.mergeSectionPlates(_section.Name, _section.Guid);
        }


        // this.detectSectionReferenceLines(asset);

        this.plates = [];
        this.stiffeners = [];
        this.facePlates = [];
        this.brackets = [];
        this.webs = [];
        this.flanges = [];

        for (let i = 0; i < region.sections.length; i++) {
            const _section = region.sections[i];
            this.setCurrentData(_section, false);
        }

        let stiffenersToWorker: IStiffeners[];
        stiffenersToWorker = [];
        for (let i = 0; i < this.stiffeners.length; i++) {
            let stiffenerIterator: IStiffeners = new IStiffeners()
            stiffenerIterator.SurfaceDesign = new ISurfaceDesign()
            stiffenerIterator.properties = new IProperty()
            stiffenerIterator.SurfaceDesign.geometry = new IGeometry()
            stiffenerIterator.section = new ISection();
            stiffenerIterator.SurfaceDesign.geometry.boundingBox = this.serializeBoundingBox(this.stiffeners[i].SurfaceDesign.geometry.boundingBox)
            stiffenerIterator.SurfaceDesign.geometry.attributes = this.serializeAttributesForWorker(this.stiffeners[i].SurfaceDesign.geometry.attributes)
            stiffenerIterator.SurfaceDesign.geometry.index = this.serializeIndexForWorker(this.stiffeners[i].SurfaceDesign.geometry.index)
            stiffenerIterator.SurfaceDesign.name = this.stiffeners[i].SurfaceDesign.name
            stiffenerIterator.section.Guid = this.stiffeners[i].section.Guid
            stiffenerIterator.properties = this.stiffeners[i].properties
            stiffenersToWorker.push(stiffenerIterator)
        }

        let bracketsToWorker: IBrackets[];
        bracketsToWorker = [];
        // for (let i = 0; i < this.brackets.length; i++) {
        //     let bracketsIterator: IBrackets = new IBrackets()
        //     bracketsIterator.SurfaceDesign = new ISurfaceDesign()
        //     bracketsIterator.properties = new IProperty()
        //     bracketsIterator.SurfaceDesign.geometry = new IGeometry()
        //     bracketsIterator.section = new ISection();
        //     bracketsIterator.SurfaceDesign.geometry.boundingBox = this.brackets[i].SurfaceDesign.geometry.boundingBox
        //     bracketsIterator.SurfaceDesign.geometry.attributes = this.brackets[i].SurfaceDesign.geometry.attributes
        //     bracketsIterator.SurfaceDesign.geometry.index = this.brackets[i].SurfaceDesign.geometry.index
        //     bracketsIterator.SurfaceDesign.name = this.brackets[i].SurfaceDesign.name
        //     bracketsIterator.section.Guid = this.brackets[i].section.Guid
        //     bracketsIterator.properties = this.brackets[i].properties
        //     bracketsToWorker.push(bracketsIterator)
        // }
        // this.detectLongitudinalsReferenceLines(region.Guid, 0.0851);

        const sectionGeometryWorker = this.sectionGeometry.map((section) => ({
            id: section.id,
            name: section.name,
            geometry: this.serializeGeometryForWorker(section.geometry),
            referenceLines: section.referenceLines ?? [],
        }))

        if (typeof Worker !== 'undefined') {
            // Create a new
            const worker = new Worker(new URL('./lines-computer-helper.worker.ts', import.meta.url), { type: 'module' });
            worker.onmessage = ({ data }) => {
                console.log("Calculated reference lines:");
                data.AssetId = this.assetManager.asset?.assetInfo?.AssetId
                data.RegionId = this.currentRegionId
                console.log(data);
                this.digitalTwinService.addLinesGeometry(data, asset.assetInfo.AssetId).then(() => {
                    // console.log('Finished adding referenceLines!')
                    // this.snackBar.open('Finished adding referenceLines', '', {
                    //     horizontalPosition: 'right',
                    //     verticalPosition: 'bottom',
                    //     duration: 3000
                    // });
                });
            };

            worker.postMessage({
                sectionGeometryWorker,
                bracketsToWorker,
                stiffenersToWorker
            });
        }
    }

    public async executeReferenceLines(asset: Asset) {
        this.resetData();
        this.bBox = new THREE.Box3();
        if (asset.mode === 'REGIONS' || asset.mode === 'SECTIONS') {
            this.setRunningUtilities(true);
            let _currentRegions = asset.GetStructureVisibleRegions();
            if (_currentRegions) {
                const _currentRegion = _currentRegions[0];
                this.linesToUpdate.RegionId = _currentRegion.Guid;
                this.refelinesToUpdate.RegionId = _currentRegion.Guid;
                this.currentRegionId = _currentRegion.Guid;
                this.setRegionBoundingBoxAndCenter(_currentRegion);

                for (let i = 0; i < _currentRegion.sections.length; i++) {
                    const _section = _currentRegion.sections[i];
                    if (_section.visible) {
                        this.setCurrentData(_section, true);
                        this.mergeSectionPlates(_section.Name, _section.Guid);
                    }
                }


                // this.detectSectionReferenceLines(asset);

                this.plates = [];
                this.stiffeners = [];
                this.facePlates = [];
                this.brackets = [];
                this.webs = [];
                this.flanges = [];

                for (let i = 0; i < _currentRegion.sections.length; i++) {
                    const _section = _currentRegion.sections[i];
                    if (_section.visible) {
                        this.setCurrentData(_section, false);
                    }
                }

                let stiffenersToWorker: IStiffeners[];
                stiffenersToWorker = [];
                for (let i = 0; i < this.stiffeners.length; i++) {
                    let stiffenerIterator: IStiffeners = new IStiffeners()
                    stiffenerIterator.SurfaceDesign = new ISurfaceDesign()
                    stiffenerIterator.properties = new IProperty()
                    stiffenerIterator.SurfaceDesign.geometry = new IGeometry()
                    stiffenerIterator.section = new ISection();
                    stiffenerIterator.SurfaceDesign.geometry.boundingBox = this.serializeBoundingBox(this.stiffeners[i].SurfaceDesign.geometry.boundingBox)
                    stiffenerIterator.SurfaceDesign.geometry.attributes = this.serializeAttributesForWorker(this.stiffeners[i].SurfaceDesign.geometry.attributes)
                    stiffenerIterator.SurfaceDesign.geometry.index = this.serializeIndexForWorker(this.stiffeners[i].SurfaceDesign.geometry.index)
                    stiffenerIterator.SurfaceDesign.name = this.stiffeners[i].SurfaceDesign.name
                    stiffenerIterator.section.Guid = this.stiffeners[i].section.Guid
                    stiffenerIterator.properties = this.stiffeners[i].properties
                    stiffenersToWorker.push(stiffenerIterator)
                }

                let bracketsToWorker: IBrackets[];
                bracketsToWorker = [];
                // for (let i = 0; i < this.brackets.length; i++) {
                //     let bracketsIterator: IBrackets = new IBrackets()
                //     bracketsIterator.SurfaceDesign = new ISurfaceDesign()
                //     bracketsIterator.properties = new IProperty()
                //     bracketsIterator.SurfaceDesign.geometry = new IGeometry()
                //     bracketsIterator.section = new ISection();
                //     bracketsIterator.SurfaceDesign.geometry.boundingBox = this.brackets[i].SurfaceDesign.geometry.boundingBox
                //     bracketsIterator.SurfaceDesign.geometry.attributes = this.brackets[i].SurfaceDesign.geometry.attributes
                //     bracketsIterator.SurfaceDesign.geometry.index = this.brackets[i].SurfaceDesign.geometry.index
                //     bracketsIterator.SurfaceDesign.name = this.brackets[i].SurfaceDesign.name
                //     bracketsIterator.section.Guid = this.brackets[i].section.Guid
                //     bracketsIterator.properties = this.brackets[i].properties
                //     bracketsToWorker.push(bracketsIterator)
                // }
                // this.detectLongitudinalsReferenceLines(_currentRegion.Guid, 0.0851);

                const sectionGeometryWorker = this.sectionGeometry.map((section) => ({
                    id: section.id,
                    name: section.name,
                    geometry: this.serializeGeometryForWorker(section.geometry),
                    referenceLines: section.referenceLines ?? [],
                }))

                if (typeof Worker !== 'undefined') {
                    // Create a new
                    const worker = new Worker(new URL('./lines-computer-helper.worker.ts', import.meta.url), { type: 'module', name: 'ReferenceLines-worker' });
                    worker.onmessage = ({ data }) => {
                        console.log("Calculated reference lines:");
                        data.AssetId = this.assetManager.asset?.assetInfo?.AssetId
                        data.RegionId = this.currentRegionId
                        console.log(data);
                        this.digitalTwinService.addLinesGeometry(data, asset.assetInfo.AssetId).then(() => {
                            console.log('Finished adding referenceLines!')
                            // this.snackBar.open('Finished adding referenceLines', '', {
                            //     horizontalPosition: 'right',
                            //     verticalPosition: 'bottom',
                            //     duration: 3000
                            // });
                        });
                        this.setRunningUtilities(false);
                    };

                    worker.postMessage({
                        sectionGeometryWorker,
                        bracketsToWorker,
                        stiffenersToWorker
                    });
                } else {
                    this.setRunningUtilities(false);
                    // Web Workers are not supported in this environment.
                    // You should add a fallback so that your program still executes correctly.
                }



            }
        }
    }


    private mergeSectionPlates(name: string, id: string) {
        const geometries = [];
        for (const plate of this.plates) {
            geometries.push(plate.SurfaceDesign.geometry);
        }
        if (geometries?.length > 0) {
    const sectionGeometry = mergeGeometries(geometries);
            if (sectionGeometry) {
                sectionGeometry.computeBoundingBox();
                this.sectionGeometry.push({ id: id, name: name, geometry: sectionGeometry, referenceLines: [] });
            }
        }
    }

    private findFacePlatesIntersectionWithPlates() {
        const facePlates = [];
        for (let i = 0; i < this.facePlates.length; i++) {
            const facePlate = this.facePlates[i];
            // console.log('------------------------------------------------------------------------------')

            if (facePlate.Guid === "68e9b51d9fcd4ca891d1561f39786c5a") {
                ;
            }
            if (!this.checkIntersectionWithPlates4FacePles(this.plates, facePlate, 0.051, true)) {
                // stiffener.material = this.graphics.redMaterial
                facePlates.push(facePlate);
            }
            else {
                // stiffener.material = this.graphics.yellowMaterial
                // flanges.push(facePlate);
            }
        }
    }

    private findFlangesForStiffeners(elements: any, structures: any, order: boolean, tolerance: number) {
        const stiffeners = [];
        const flanges = [];
        for (let i = 0; i < elements.length; i++) {
            const stiffener = elements[i];
            // console.log('------------------------------------------------------------------------------')

            if (order)
                if (this.checkIntersectionWithPlates(structures, stiffener, tolerance, false)) {
                    // stiffener.material = this.graphics.redMaterial
                    stiffeners.push(stiffener);
                }
                else {
                    // stiffener.material = this.graphics.yellowMaterial
                    flanges.push(stiffener);
                }
            else
                if (!this.checkIntersectionWithPlates4FacePles(structures, stiffener, tolerance, false)) {
                    // stiffener.material = this.graphics.redMaterial
                    stiffeners.push(stiffener);
                }
                else {
                    // stiffener.material = this.graphics.yellowMaterial
                    flanges.push(stiffener);
                }
        }
        return flanges;
    }

    private checkIntersectionWithPlates(structures: any[], stiffener: any, tolerance: number, addLine: boolean): boolean {
        let line: Array<THREE.Vector3> = new Array<THREE.Vector3>();
    var pointsOfIntersection = new Geometry();
        for (let i = 0; i < structures.length; i++) {
            const plate = structures[i];
            if (plate.Guid == "e843b19319b74bf9af411608364c68f3" && stiffener.Guid === "324cb17c842941b4b40e9fc3bf83d91a") {

            }
            // plate.visible = false;
            if (this.boxesDistance(plate.SurfaceDesign.geometry.boundingBox, stiffener.SurfaceDesign.geometry.boundingBox) <= 0.5) {// &&
                // if (plate.geometry.boundingBox.intersectsBox(stiffener.geometry.boundingBox)) {
                const localLine = this.checkTriangleCollision(plate.SurfaceDesign, stiffener.SurfaceDesign, tolerance)[0];
                if (localLine.length > 0) {
                    line.push.apply(line, localLine);
                }
            }
        }
        if (line.length > 1) {
            line = this.uniqueValues(line);
            if (line.length == 1) {
                return false;
            }

            if(line.length === 2  ){
                let dd = line[0].distanceTo(line[1]);
                if(dd <= 0.19)
                    return false;
            }
            //stiffener.visible = false;
            let sortedLine = this.sortPointsToFormLineFacePlates(line);
            let dista = sortedLine[0].distanceTo(sortedLine[sortedLine.length-1])
            if(dista <=0.19)
                return false;
            stiffener.SurfaceDesign.userData.newLine = sortedLine;

            // stiffener.userData.isWeb = true;
            const newLine = []
            for (const vec of stiffener.SurfaceDesign.userData.newLine) {
                pointsOfIntersection.vertices.push(vec);
                newLine.push(vec.x);
                newLine.push(vec.y);
                newLine.push(vec.z);
            }
            stiffener.SurfaceDesign.userData.sendLine = newLine
            if (addLine)
                this.linesToUpdate.Lines.push({ Item1: stiffener.Guid, Item2: stiffener.SurfaceDesign.userData.sendLine, Item3: null, Item4: null, Item5: false });

            var lines = new THREE.LineSegments(pointsOfIntersection, new THREE.LineBasicMaterial({
                color: 0xff0000, side: THREE.DoubleSide
            }));
            // stiffener.SurfaceDesign.add(lines);
            return true;
        }
        else {
            for (let i = 0; i < structures.length; i++) {
                const plate = structures[i];
                // plate.visible = false;
                if (this.boxesDistance(plate.SurfaceDesign.geometry.boundingBox, stiffener.SurfaceDesign.geometry.boundingBox) <= 1.5) {// &&
                    // if (plate.geometry.boundingBox.intersectsBox(stiffener.geometry.boundingBox)) {
                    const localLine = this.checkTriangleCollision(plate.SurfaceDesign, stiffener.SurfaceDesign, tolerance)[0];
                    if (localLine.length > 0) {
                        line.push.apply(line, localLine);
                    }
                }
            }

            if (line.length > 1) {
                line = this.uniqueValues(line);
                if (line.length == 1) {
                    return false;
                }
                //stiffener.visible = false;
                stiffener.SurfaceDesign.userData.newLine = this.sortPointsToFormLineFacePlates2(line);
                // stiffener.userData.isWeb = true;
                const newLine = []
                for (const vec of stiffener.SurfaceDesign.userData.newLine) {
                    pointsOfIntersection.vertices.push(vec);
                    newLine.push(vec.x);
                    newLine.push(vec.y);
                    newLine.push(vec.z);
                }
                stiffener.SurfaceDesign.userData.sendLine = newLine
                if (addLine)
                    this.linesToUpdate.Lines.push({ Item1: stiffener.Guid, Item2: stiffener.SurfaceDesign.userData.sendLine, Item3: null, Item4: null, Item5: false });

                var lines = new THREE.LineSegments(pointsOfIntersection, new THREE.LineBasicMaterial({
                    color: 0xff0000, side: THREE.DoubleSide
                }));
                // this.graphics.scene.add(lines);
                // stiffener.SurfaceDesign.add(lines);
                return true;
            }
        }
        return false;
    }

    private checkIntersectionWithPlates4FacePles(structures: any[], stiffener: any, tolerance: number, addLine: boolean): boolean {
        let line: Array<THREE.Vector3> = new Array<THREE.Vector3>();
    var pointsOfIntersection = new Geometry();
        for (let i = 0; i < structures.length; i++) {
            const plate = structures[i];
            // plate.visible = false;
            if (this.boxesDistance(plate.SurfaceDesign.geometry.boundingBox, stiffener.SurfaceDesign.geometry.boundingBox) <= 0.5) {// &&
                // if (plate.geometry.boundingBox.intersectsBox(stiffener.geometry.boundingBox)) {
                const localLine = this.checkTriangleCollision(stiffener.SurfaceDesign, plate.SurfaceDesign, tolerance)[0];
                if (localLine.length > 0) {
                    line.push.apply(line, localLine);
                }
            }
        }
        if (line.length > 1) {
            line = this.uniqueValues(line);
            if (line.length == 1) {
                return false;
            }
            //stiffener.visible = false;
            stiffener.SurfaceDesign.userData.newLine = this.sortPointsToFormLineFacePlates(line);
            // stiffener.userData.isWeb = true;
            const newLine = []
            for (const vec of stiffener.SurfaceDesign.userData.newLine) {
                pointsOfIntersection.vertices.push(vec);
                newLine.push(vec.x);
                newLine.push(vec.y);
                newLine.push(vec.z);
            }
            stiffener.SurfaceDesign.userData.sendLine = newLine
            if (addLine)
                this.linesToUpdate.Lines.push({ Item1: stiffener.Guid, Item2: stiffener.SurfaceDesign.userData.sendLine, Item3: null, Item4: null, Item5: false });

            var lines = new THREE.LineSegments(pointsOfIntersection, new THREE.LineBasicMaterial({
                color: 0xff0000, side: THREE.DoubleSide
            }));
            stiffener.SurfaceDesign.add(lines);
            return true;
        }
        else {
            for (let i = 0; i < structures.length; i++) {
                const plate = structures[i];
                // plate.visible = false;
                if (this.boxesDistance(plate.SurfaceDesign.geometry.boundingBox, stiffener.SurfaceDesign.geometry.boundingBox) <= 1.5) {// &&
                    // if (plate.geometry.boundingBox.intersectsBox(stiffener.geometry.boundingBox)) {
                    const localLine = this.checkTriangleCollision(plate.SurfaceDesign, stiffener.SurfaceDesign, tolerance)[0];
                    if (localLine.length > 0) {
                        line.push.apply(line, localLine);
                    }
                }
            }

            if (line.length > 1) {
                line = this.uniqueValues(line);
                if (line.length == 1) {
                    return false;
                }
                //stiffener.visible = false;
                stiffener.SurfaceDesign.userData.newLine = this.sortPointsToFormLineFacePlates2(line);
                // stiffener.userData.isWeb = true;
                const newLine = []
                for (const vec of stiffener.SurfaceDesign.userData.newLine) {
                    pointsOfIntersection.vertices.push(vec);
                    newLine.push(vec.x);
                    newLine.push(vec.y);
                    newLine.push(vec.z);
                }
                stiffener.SurfaceDesign.userData.sendLine = newLine
                if (addLine)
                    this.linesToUpdate.Lines.push({ Item1: stiffener.Guid, Item2: stiffener.SurfaceDesign.userData.sendLine, Item3: null, Item4: null, Item5: false });

                var lines = new THREE.LineSegments(pointsOfIntersection, new THREE.LineBasicMaterial({
                    color: 0xff0000, side: THREE.DoubleSide
                }));
                // this.graphics.scene.add(lines);
                stiffener.SurfaceDesign.add(lines);
                return true;
            }
        }
        return false;
    }

    private matchFlangesWithWebs(elements, addLine: boolean, normal?: THREE.Vector3): any {
        let fl = []
        for (const item of elements) {

            if (item.SurfaceDesign.userData.isFlange) {
                const websToMerge = [];
                for (let i = 0; i < this.webs.length; i++) {
                    const web = this.webs[i];
                    if(!web.SurfaceDesign.userData.Flanges)
                    web.SurfaceDesign.userData.Flanges = [];
                    // var newMat = GraphicsMaterials.randomMaterial.clone();
                    // web.SurfaceDesign.material = newMat;

                    if (this.boxesDistance(web.SurfaceDesign.geometry.boundingBox, item.SurfaceDesign.geometry.boundingBox) <= 0.1) {

                        let size1 = new THREE.Vector3();
                        let size2 = new THREE.Vector3();
                        web.SurfaceDesign.geometry.boundingBox.getSize(size1);
                        item.SurfaceDesign.geometry.boundingBox.getSize(size2);
                        if (Math.max(...[size1.x, size1.y, size1.z]) > Math.max(...[size2.x, size2.y, size2.z])) {
                            let localLine = this.checkTriangleCollision(web.SurfaceDesign, item.SurfaceDesign, 0.091)[0];
                            if (localLine.length >= 1 && localLine[0] !== undefined)
                                websToMerge.push(web);
                        }
                        else {
                            let localLine = this.checkTriangleCollision(item.SurfaceDesign, web.SurfaceDesign, 0.091)[0];
                            if (localLine.length >= 1 && localLine[0] !== undefined)
                                websToMerge.push(web);
                        }
                    }
                }

                if (websToMerge.length > 1) {
                    const geometries = [];

                    let finalLine = []
                    for (const web of websToMerge) {
                        for (const p of web.SurfaceDesign.userData.newLine) {
                            finalLine.push(p);
                        }
                    }
                    for (const web of websToMerge) {
                        geometries.push(web.SurfaceDesign.geometry);
                    }
                    if (geometries?.length > 0) {
    const webGeometries = mergeGeometries(geometries);
                        if (webGeometries) {
                            webGeometries.computeBoundingBox();

                            const webMesh = new THREE.Mesh(webGeometries, GraphicsMaterials.basicMaterial);

                            if (this.boxesDistance(webGeometries.boundingBox, item.SurfaceDesign.geometry.boundingBox) <= 0.1) {
                                let localLine = this.checkTriangleCollision(webMesh, item.SurfaceDesign, 0.051)[0];

                                if (localLine.length > 1) {
                                    fl.push(item);

                                    localLine = this.uniqueValues(localLine);
                                    if (localLine.length == 1) {
                                        continue;
                                    }

                                    //stiffener.visible = false;
                                    localLine = this.sortPointsToFormLineFacePlates(localLine);
                                    if (localLine.length == 1 || (localLine.length > 1 && localLine[0].distanceTo(localLine[localLine.length - 1]) <= 0.05)) {
                                        continue;
                                    }
                                    const found = this.flanges.find(x => x.Guid === item.Guid);
                                    if (!found) {
                                        this.flanges.push(item);
                                        // item.SurfaceDesign.material = newMat;
                                    }
                                    // if (addLine)
                                    {
                                        item.SurfaceDesign.userData.newFlangeLine.push.apply(item.SurfaceDesign.userData.newFlangeLine, localLine);
                                        // web.SurfaceDesign.userData.Flanges.push(item.Guid);
                                        item.SurfaceDesign.userData.isFlange = false;
                                    }
                                    // else {
                                    //     const box3: THREE.Box3 = item.SurfaceDesign.geometry.boundingBox.clone() as THREE.Box3;
                                    //     // box3.expandByVector(normal.multiplyScalar(3));
                                    //     const points = [];

                                    //     finalLine = this.uniqueValues(finalLine);
                                    //     if (finalLine.length == 1) {
                                    //         continue;
                                    //     }
                                    //     finalLine = this.sortPointsToFormLineFacePlates(finalLine);

                                    //     if (finalLine.length == 1 || (finalLine.length > 1 && finalLine[0].distanceTo(finalLine[finalLine.length - 1]) <= 0.05)) {
                                    //         continue;
                                    //     }
                                    //     //stiffener.visible = false;

                                    //     // const triangles: Array<THREE.Triangle> = []

                                    //     // for (let i = 0; i < item.SurfaceDesign.geometry.index.array.length; i += 3) {
                                    //     //     const vA = new THREE.Vector3(item.SurfaceDesign.geometry.attributes.position.array[item.SurfaceDesign.geometry.index.array[i] * 3],
                                    //     //         item.SurfaceDesign.geometry.attributes.position.array[item.SurfaceDesign.geometry.index.array[i] * 3 + 1],
                                    //     //         item.SurfaceDesign.geometry.attributes.position.array[item.SurfaceDesign.geometry.index.array[i] * 3 + 2]);

                                    //     //     const vB = new THREE.Vector3(item.SurfaceDesign.geometry.attributes.position.array[item.SurfaceDesign.geometry.index.array[i + 1] * 3],
                                    //     //         item.SurfaceDesign.geometry.attributes.position.array[item.SurfaceDesign.geometry.index.array[i + 1] * 3 + 1],
                                    //     //         item.SurfaceDesign.geometry.attributes.position.array[item.SurfaceDesign.geometry.index.array[i + 1] * 3 + 2]);

                                    //     //     const vC = new THREE.Vector3(item.SurfaceDesign.geometry.attributes.position.array[item.SurfaceDesign.geometry.index.array[i + 2] * 3],
                                    //     //         item.SurfaceDesign.geometry.attributes.position.array[item.SurfaceDesign.geometry.index.array[i + 2] * 3 + 1],
                                    //     //         item.SurfaceDesign.geometry.attributes.position.array[item.SurfaceDesign.geometry.index.array[i + 2] * 3 + 2]);

                                    //     //     const tri = new THREE.Triangle(vA, vB, vC);
                                    //     //     if (tri.getArea() === 0) {
                                    //     //         continue;
                                    //     //     }
                                    //     //     triangles.push(tri);
                                    //     // }


                                    //     // const lines: Array<Line3> = []
                                    //     // const vectors = []
                                    //     // for (let i = 0; i < item.SurfaceDesign.children.length; i++) {
                                    //     //     const child = item.SurfaceDesign.children[i];
                                    //     //     for (let j = 0; j < child.geometry.attributes.position.length; j += 3) {
                                    //     //         const v1 = child.geometry.attributes.position.array[j];
                                    //     //         const v2 = child.geometry.attributes.position.array[j + 1];
                                    //     //         const v3 = child.geometry.attributes.position.array[j + 2];
                                    //     //         const vt = new THREE.Vector3(v1, v2, v3);
                                    //     //         vectors.push(vt);
                                    //     //     }
                                    //     // }

                                    //     // for (let i = 0; i < vectors.length - 1; i++) {
                                    //     //     const v1 = vectors[i];
                                    //     //     const v2 = vectors[i + 1];
                                    //     //     lines.push(new THREE.Line3(v1, v2))
                                    //     // }



                                    //     for (const p of finalLine) {

                                    //         // triangles.map(x => )
                                    //         // let pp = new THREE.Vector3();
                                    //         // const closest = triangles.reduce(
                                    //         //     (acc, loc) =>
                                    //         //         acc.closestPointToPoint(p, pp).distanceTo(p) < loc.closestPointToPoint(p, pp).distanceTo(p)
                                    //         //             ? acc
                                    //         //             : loc
                                    //         // )

                                    //         let cP = new THREE.Vector3();

                                    //         // closest.closestPointToPoint(p, cP)
                                    //         box3.clampPoint(p, cP);
                                    //         points.push(cP);
                                    //     }

                                    //     // const line = new THREE.Line3(web.SurfaceDesign.userData.newLine[0],
                                    //     //     web.SurfaceDesign.userData.newLine[web.SurfaceDesign.userData.newLine.length - 1]);
                                    //     // const finalPoints = [];
                                    //     // for (const p of points) {
                                    //     //     const finalPoint = new THREE.Vector3();
                                    //     //     line.closestPointToPoint(p, true, finalPoint);
                                    //     //     finalPoints.push(finalPoint)
                                    //     // }
                                    //     // web.SurfaceDesign.userData.sendLine
                                    //     item.SurfaceDesign.userData.newFlangeLine.push.apply(item.SurfaceDesign.userData.newFlangeLine, points);
                                    //     item.SurfaceDesign.userData.isFlange = false;
                                    // }
                                }
                            }
                        }
                    }
                }
                else if (websToMerge.length === 1) {
                    const web = websToMerge[0];
                    if (this.boxesDistance(web.SurfaceDesign.geometry.boundingBox, item.SurfaceDesign.geometry.boundingBox) <= 0.1) {
                        let localLine = this.checkTriangleCollision(web.SurfaceDesign, item.SurfaceDesign, 0.1)[0];

                        if (localLine.length > 1) {
                            fl.push(item);

                            localLine = this.uniqueValues(localLine);
                            if (localLine.length == 1 || (localLine.length > 1 && localLine[0].distanceTo(localLine[localLine.length - 1]) <= 0.05)) {
                                continue;
                            }
                            //stiffener.visible = false;
                            localLine = this.sortPointsToFormLineFacePlates(localLine);
                            const found = this.flanges.find(x => x.Guid === item.Guid);
                            if (!found) {
                                this.flanges.push(item);
                                // item.SurfaceDesign.material = newMat;
                            }
                            if (addLine) {
                                item.SurfaceDesign.userData.newFlangeLine.push.apply(item.SurfaceDesign.userData.newFlangeLine, localLine);
                                web.SurfaceDesign.userData.Flanges.push(item.Guid);
                                item.SurfaceDesign.userData.isFlange = true;
                            }
                            else {
                                const box3: THREE.Box3 = item.SurfaceDesign.geometry.boundingBox.clone() as THREE.Box3;
                                // box3.expandByVector(normal.multiplyScalar(3));
                                const points = [];
                                for (const p of web.SurfaceDesign.userData.newLine) {
                                    let cP = new THREE.Vector3();
                                    box3.clampPoint(p, cP);
                                    points.push(cP);
                                }

                                // const line = new THREE.Line3(web.SurfaceDesign.userData.newLine[0],
                                //     web.SurfaceDesign.userData.newLine[web.SurfaceDesign.userData.newLine.length - 1]);
                                // const finalPoints = [];
                                // for (const p of points) {
                                //     const finalPoint = new THREE.Vector3();
                                //     line.closestPointToPoint(p, true, finalPoint);
                                //     finalPoints.push(finalPoint)
                                // }
                                // web.SurfaceDesign.userData.sendLine
                                item.SurfaceDesign.userData.newFlangeLine.push.apply(item.SurfaceDesign.userData.newFlangeLine, points);
                                item.SurfaceDesign.userData.isFlange = false;
                            }
                        }
                    }
                }
            }
        }

        // for (let i = 0; i < this.webs.length; i++) {
        //     const web = this.webs[i];
        //     web.SurfaceDesign.userData.Flanges = [];
        //     var randomColor = Math.floor(Math.random() * 16777215).toString(16);
        //     const newMat = new THREE.MeshBasicMaterial({ color: Number('0x' + randomColor), side: THREE.DoubleSide });
        //     web.SurfaceDesign.material = newMat;
        //     for (const item of elements) {
        //         if (item.SurfaceDesign.userData.isFlange) {
        //             item.SurfaceDesign.userData.isFlange = true;
        //             if (this.boxesDistance(web.SurfaceDesign.geometry.boundingBox, item.SurfaceDesign.geometry.boundingBox) <= 0.1) {
        //                 let localLine = this.checkTriangleCollision(web.SurfaceDesign, item.SurfaceDesign, 0.1)[0];

        //                 if (localLine.length > 1) {

        //                     localLine = this.uniqueValues(localLine);
        //                     if (localLine.length == 1 || (localLine.length > 1 && localLine[0].distanceTo(localLine[localLine.length - 1]) <= 0.05)) {
        //                         continue;
        //                     }
        //                     //stiffener.visible = false;
        //                     localLine = this.sortPointsToFormLineFacePlates(localLine);
        //                     const found = this.flanges.find(x => x.Guid === item.Guid);
        //                     if (!found) {
        //                         this.flanges.push(item);
        //                         item.SurfaceDesign.material = newMat;
        //                     }
        //                     if (addLine) {
        //                         item.SurfaceDesign.userData.newFlangeLine.push.apply(item.SurfaceDesign.userData.newFlangeLine, localLine);
        //                         web.SurfaceDesign.userData.Flanges.push(item.Guid);
        //                         item.SurfaceDesign.userData.isFlange = true;
        //                     }
        //                     else {
        //                         const box3: THREE.Box3 = item.SurfaceDesign.geometry.boundingBox.clone() as THREE.Box3;
        //                         // box3.expandByVector(normal.multiplyScalar(3));
        //                         const points = [];
        //                         for (const p of web.SurfaceDesign.userData.newLine) {
        //                             let cP = new THREE.Vector3();
        //                             box3.clampPoint(p, cP);
        //                             points.push(cP);
        //                         }

        //                         // const line = new THREE.Line3(web.SurfaceDesign.userData.newLine[0],
        //                         //     web.SurfaceDesign.userData.newLine[web.SurfaceDesign.userData.newLine.length - 1]);
        //                         // const finalPoints = [];
        //                         // for (const p of points) {
        //                         //     const finalPoint = new THREE.Vector3();
        //                         //     line.closestPointToPoint(p, true, finalPoint);
        //                         //     finalPoints.push(finalPoint)
        //                         // }
        //                         // web.SurfaceDesign.userData.sendLine
        //                         item.SurfaceDesign.userData.newFlangeLine.push.apply(item.SurfaceDesign.userData.newFlangeLine, points);
        //                         item.SurfaceDesign.userData.isFlange = false;
        //                     }
        //                 }
        //             }
        //         }
        //     }
        //     const found = this.linesToUpdate.Lines.find(x => x.Item1 === web.Guid);
        //     if (!found) {

        //     }

        // }

        // const certainWebs = []
        // const possibleFlanges = []

        // for (const web of this.webs) {
        //     if (!web.SurfaceDesign.userData.newLine || web.SurfaceDesign.userData.newLine.length === 0) {
        //         web.SurfaceDesign.userData.isFlange = true;
        //         possibleFlanges.push(web);
        //     }
        //     else {
        //         certainWebs.push(web);
        //     }
        // }
        // const canonicalNormal = Util.LargestCanonicalAxisDirection(normal);
        // if (canonicalNormal === 'x') {
        //     this.webs = certainWebs;
        //     for (let i = 0; i < certainWebs.length; i++) {
        //         const web = certainWebs[i];
        //         // web.SurfaceDesign.userData.Flanges = [];
        //         var randomColor = Math.floor(Math.random() * 16777215).toString(16);
        //         const newMat = new THREE.MeshBasicMaterial({ color: Number('0x' + randomColor), side: THREE.DoubleSide });
        //         web.SurfaceDesign.material = newMat;

        //         for (const item of possibleFlanges) {
        //             if (item.SurfaceDesign.userData.isFlange) {
        //                 item.SurfaceDesign.userData.isFlange = true;
        //                 if (this.boxesDistance(web.SurfaceDesign.geometry.boundingBox, item.SurfaceDesign.geometry.boundingBox) <= 0.5) {
        //                     const result = this.checkTriangleCollision(web.SurfaceDesign, item.SurfaceDesign, 0.1);
        //                     const localLine = result[0];
        //                     const lineIndexes = result[1];
        //                     if (localLine.length > 1 && (new Set(lineIndexes)).size == (new Set(item.SurfaceDesign.geometry.index.array)).size) {
        //                         const found = this.flanges.find(x => x.Guid === item.Guid);
        //                         if (!found) {
        //                             this.flanges.push(item);
        //                             item.SurfaceDesign.material = newMat;
        //                         }
        //                         if (addLine) {
        //                             // item.SurfaceDesign.userData.newFlangeLine.push.apply(item.SurfaceDesign.userData.newFlangeLine, localLine);
        //                             web.SurfaceDesign.userData.Flanges.push(item.Guid);
        //                             item.SurfaceDesign.userData.isFlange = true;
        //                         }
        //                         else {
        //                             const box3: THREE.Box3 = item.SurfaceDesign.geometry.boundingBox.clone() as THREE.Box3;
        //                             // box3.expandByVector(normal.multiplyScalar(3));
        //                             const points = [];
        //                             for (const p of web.SurfaceDesign.userData.newLine) {
        //                                 let cP = new THREE.Vector3();
        //                                 box3.clampPoint(p, cP);
        //                                 points.push(cP);
        //                             }

        //                             // const line = new THREE.Line3(web.SurfaceDesign.userData.newLine[0],
        //                             //     web.SurfaceDesign.userData.newLine[web.SurfaceDesign.userData.newLine.length - 1]);
        //                             // const finalPoints = [];
        //                             // for (const p of points) {
        //                             //     const finalPoint = new THREE.Vector3();
        //                             //     line.closestPointToPoint(p, true, finalPoint);
        //                             //     finalPoints.push(finalPoint)
        //                             // }
        //                             // web.SurfaceDesign.userData.sendLine
        //                             item.SurfaceDesign.userData.newFlangeLine.push.apply(item.SurfaceDesign.userData.newFlangeLine, points);
        //                             item.SurfaceDesign.userData.isFlange = false;
        //                         }
        //                     }
        //                 }
        //             }
        //         }
        //         const found = this.linesToUpdate.Lines.find(x => x.Item1 === web.Guid);
        //         if (!found) {

        //         }

        //     }
        // }

        for (const flange of this.flanges) {

            let newLine = []
            for (const vec of flange.SurfaceDesign.userData.newFlangeLine) {
                newLine.push(vec.x);
                newLine.push(vec.y);
                newLine.push(vec.z);
            }

            flange.SurfaceDesign.userData.sendLine = newLine
            if (flange.SurfaceDesign.userData.sendLine && flange.SurfaceDesign.userData.sendLine.length > 0)
                this.linesToUpdate.Lines.push({ Item1: flange.Guid, Item2: flange.SurfaceDesign.userData.sendLine, Item3: null, Item4: null, Item5: flange.SurfaceDesign.userData.isFlange });
            else
                this.linesToUpdate.Lines.push({ Item1: flange.Guid, Item2: null, Item3: null, Item4: null, Item5: flange.SurfaceDesign.userData.isFlange });
        }

        return fl;
    }

    private matchStiffenerFlangesWithWebs(elements, addLine: boolean, normal?: THREE.Vector3): any {
        let fl = []
        for (const item of elements) {
            if(item.Guid === '9c6183fd18f846048576e289bf66ab5d'){
                const asdf = 0
            }
            if (item.SurfaceDesign.userData.isFlange) {

                // const newMat = GraphicsMaterials.randomMaterial.clone();
                // item.SurfaceDesign.material = newMat;
                item.SurfaceDesign.userData.isFlange = true;

                let localLineIndex = this.checkConectionLine(item, elements, addLine, fl);
                let localLine = localLineIndex.localLine;
                let index = localLineIndex.index;
                if (localLine.length !== 0 /*&& Math.min(...centroidDistances) !== Number.MAX_SAFE_INTEGER*/) {
                    this.flanges.push(item);
                    if (elements[index].SurfaceDesign.userData.Flanges?.length === 0) {
                        // elements[index].SurfaceDesign.material = newMat;
                    } else {
                        item.SurfaceDesign.material = GraphicsMaterials.colorToBasicMaterial(elements[index].SurfaceDesign.material.color);
                    }

                    if (addLine) {
                        // web.SurfaceDesign.userData.newLine
                        // item.SurfaceDesign.userData.newFlangeLine.push.apply(item.SurfaceDesign.userData.newFlangeLine, localLine);
                        // elements[index].SurfaceDesign.userData.Flanges = elements[index].SurfaceDesign.userData.Flanges || [];
                        if(!elements[index].SurfaceDesign.userData.Flanges)
                            elements[index].SurfaceDesign.userData.Flanges = []
                        elements[index].SurfaceDesign.userData.Flanges.push(item.Guid);
                        if(this.webs.findIndex(x => x.Guid === elements[index].Guid) === -1){
                            this.webs.push(elements[index]);
                        }

                        item.SurfaceDesign.userData.isFlange = true;
                    }
                    else {
                        const box3: THREE.Box3 = item.SurfaceDesign.geometry.boundingBox.clone() as THREE.Box3;
                        // box3.expandByVector(normal.multiplyScalar(3));
                        const points = [];
                        for (const p of elements[index].SurfaceDesign.userData.newLine) {
                            let cP = new THREE.Vector3();
                            box3.clampPoint(p, cP);
                            points.push(cP);
                        }

                        item.SurfaceDesign.userData.newFlangeLine.push.apply(item.SurfaceDesign.userData.newFlangeLine, points);
                        item.SurfaceDesign.userData.isFlange = false;
                    }
                }
                else {
                    if(item.properties.Geometry.WebThickness > 0){
                        localLineIndex = this.checkConectionLine(item, this.stiffeners, addLine, fl);
                        localLine = localLineIndex.localLine;
                        index = localLineIndex.index;

                        if (localLine.length !== 0 /*&& Math.min(...centroidDistances) !== Number.MAX_SAFE_INTEGER*/) {
                            this.webs.push(item);
                            // if (elements[index].SurfaceDesign.userData.Flanges?.length === 0) {
                            //     // elements[index].SurfaceDesign.material = newMat;
                            // } else {
                            //     item.SurfaceDesign.material = GraphicsMaterials.colorToBasicMaterial(elements[index].SurfaceDesign.material.color);
                            // }

                            // if (addLine) {
                            //     // web.SurfaceDesign.userData.newLine
                            //     // item.SurfaceDesign.userData.newFlangeLine.push.apply(item.SurfaceDesign.userData.newFlangeLine, localLine);
                            //     // elements[index].SurfaceDesign.userData.Flanges = elements[index].SurfaceDesign.userData.Flanges || [];
                            //     // elements[index].SurfaceDesign.userData.Flanges.push(item.Guid);
                            //     item.SurfaceDesign.userData.isFlange = true;
                            // }
                            // else {

                            localLine = this.uniqueValues(localLine);
                            if (localLine.length == 1) {
                                return false;
                            }
                            //stiffener.visible = false;
                            item.SurfaceDesign.userData.newFlangeLine = this.sortPointsToFormLineFacePlates(localLine);
                            const newLine = [];
                            for (const vec of item.SurfaceDesign.userData.newFlangeLine) {
                                newLine.push(vec.x);
                                newLine.push(vec.y);
                                newLine.push(vec.z);
                            }
                            item.SurfaceDesign.userData.isFlange = false;
                            item.SurfaceDesign.userData.sendLine =newLine;

                            // this.linesToUpdate.Lines.push({ Item1: item.Guid, Item2: item.SurfaceDesign.userData.newLine, Item3: null, Item4: null, Item5: false });

                            // const box3: THREE.Box3 = item.SurfaceDesign.geometry.boundingBox.clone() as THREE.Box3;
                            // // box3.expandByVector(normal.multiplyScalar(3));
                            // const points = [];
                            // for (const p of elements[index].SurfaceDesign.userData.newLine) {
                            //     let cP = new THREE.Vector3();
                            //     box3.clampPoint(p, cP);
                            //     points.push(cP);
                            // }

                            // item.SurfaceDesign.userData.newFlangeLine.push.apply(item.SurfaceDesign.userData.newFlangeLine, points);
                            // item.SurfaceDesign.userData.isFlange = false;
                            // }
                        }
                        else {
                            fl.push(item);
                        }
                    }
                    else {
                        fl.push(item);
                    }
                }
            }
        }

        for (const flange of this.flanges) {
            let newLine = []
            if(flange.Guid === "be867f386f8f41feb1b33e6f5c9e99bf"){
                debugger
            }
            for (const vec of flange.SurfaceDesign.userData.newFlangeLine) {
                newLine.push(vec.x);
                newLine.push(vec.y);
                newLine.push(vec.z);
            }
            flange.SurfaceDesign.userData.sendLine = newLine
            if (flange.SurfaceDesign.userData.sendLine && flange.SurfaceDesign.userData.sendLine.length > 0)
                this.linesToUpdate.Lines.push({ Item1: flange.Guid, Item2: flange.SurfaceDesign.userData.sendLine, Item3: null, Item4: null, Item5: flange.SurfaceDesign.userData.isFlange });
            else
                this.linesToUpdate.Lines.push({ Item1: flange.Guid, Item2: null, Item3: null, Item4: null, Item5: flange.SurfaceDesign.userData.isFlange });
        }

        return fl
    }

    private matchStiffenerFlangesWithWebsTest(flanges, webs, addLine: boolean, normal?: THREE.Vector3): any {
        let fl = []
        for (const item of flanges) {
            if (item.SurfaceDesign.userData.isFlange) {
                // const newMat = GraphicsMaterials.randomMaterial.clone();
                // item.SurfaceDesign.material = newMat;
                item.SurfaceDesign.userData.isFlange = true;

                var centroidDistances: Array<number> = [];
                let itemBoxCenter: THREE.Vector3 = new THREE.Vector3();
                item.SurfaceDesign.geometry.computeBoundingBox();
                item.SurfaceDesign.geometry.boundingBox.getCenter(itemBoxCenter);
                for (const element of webs) {
                    if (!element.SurfaceDesign.userData.isFlange) {
                        let webBoxCenter: THREE.Vector3 = new THREE.Vector3();
                        element.SurfaceDesign.geometry.computeBoundingBox();
                        element.SurfaceDesign.geometry.boundingBox.getCenter(webBoxCenter);
                        centroidDistances.push(webBoxCenter.distanceTo(itemBoxCenter));
                    } else {
                        centroidDistances.push(Number.MAX_SAFE_INTEGER);
                    }
                }

                // assign web with the lowest distance to the flange centroid and intersects with it
                var localLine: Array<THREE.Vector3> = [];
                var index: number;
                do {
                    index = centroidDistances.indexOf(Math.min(...centroidDistances));
                    centroidDistances[index] = Number.MAX_SAFE_INTEGER;

                    const direction = Util.LargestCanonicalAxisDirection((item as WorkableElement).SurfaceDesign.geometry.boundingBox.min.clone().
                        sub((item as WorkableElement).SurfaceDesign.geometry.boundingBox.max.clone()));
                    // const direction = Util.LargestCanonicalAxisDirection(localLine[0].clone().sub(localLine[localLine.length - 1].clone()));
                    let pointDir = new THREE.Vector3();
                    if (webs[index].SurfaceDesign.userData.newLine.length > 0)
                        pointDir = webs[index].SurfaceDesign.userData.newLine[0].clone()
                            .sub(webs[index].SurfaceDesign.userData.newLine[webs[index].SurfaceDesign.userData.newLine.length - 1].clone());
                    else
                        pointDir = (webs[index] as WorkableElement).SurfaceDesign.geometry.boundingBox.min.clone().
                            sub((webs[index] as WorkableElement).SurfaceDesign.geometry.boundingBox.max.clone())
                    const direction2 = Util.LargestCanonicalAxisDirection(pointDir);

                    if (direction !== direction2) {
                        continue;
                    }

                    const result = this.checkTriangleCollision(webs[index].SurfaceDesign, item.SurfaceDesign, 0.15);
                    localLine = result[0];
                    if (localLine === undefined)
                        continue
                    localLine = this.uniqueValues(localLine);
                    if (localLine.length == 1 || localLine.length === 0 || localLine[0] === undefined) {
                        continue;
                    }
                    //stiffener.visible = false;
                    localLine = this.sortPointsToFormLineFacePlates(localLine);
                    if (localLine === undefined)
                        continue

                } while ((localLine === undefined || localLine.length === 0) && Math.min(...centroidDistances) !== Number.MAX_SAFE_INTEGER);

                if (localLine.length !== 0 /*&& Math.min(...centroidDistances) !== Number.MAX_SAFE_INTEGER*/) {
                    this.flanges.push(item);
                    if (webs[index].SurfaceDesign.userData.Flanges?.length === 0) {
                        // elements[index].SurfaceDesign.material = newMat;
                    } else {
                        item.SurfaceDesign.material = GraphicsMaterials.colorToBasicMaterial(webs[index].SurfaceDesign.material.color);
                    }

                    if (addLine) {
                        // web.SurfaceDesign.userData.newLine
                        // item.SurfaceDesign.userData.newFlangeLine.push.apply(item.SurfaceDesign.userData.newFlangeLine, localLine);
                        // elements[index].SurfaceDesign.userData.Flanges = elements[index].SurfaceDesign.userData.Flanges || [];
                        // elements[index].SurfaceDesign.userData.Flanges.push(item.Guid);
                        item.SurfaceDesign.userData.isFlange = true;
                    }
                    else {
                        const box3: THREE.Box3 = item.SurfaceDesign.geometry.boundingBox.clone() as THREE.Box3;
                        // box3.expandByVector(normal.multiplyScalar(3));
                        const points = [];
                        for (const p of webs[index].SurfaceDesign.userData.newLine) {
                            let cP = new THREE.Vector3();
                            box3.clampPoint(p, cP);
                            points.push(cP);
                        }

                        item.SurfaceDesign.userData.newFlangeLine.push.apply(item.SurfaceDesign.userData.newFlangeLine, points);
                        item.SurfaceDesign.userData.isFlange = false;
                    }
                }
                else {
                    fl.push(item);
                }
            }
            else {
                fl.push(item);
            }
        }

        for (const flange of this.flanges) {
            let newLine = []
            for (const vec of flange.SurfaceDesign.userData.newFlangeLine) {
                newLine.push(vec.x);
                newLine.push(vec.y);
                newLine.push(vec.z);
            }
            flange.SurfaceDesign.userData.sendLine = newLine
            if (flange.SurfaceDesign.userData.sendLine && flange.SurfaceDesign.userData.sendLine.length > 0)
                this.linesToUpdate.Lines.push({ Item1: flange.Guid, Item2: flange.SurfaceDesign.userData.sendLine, Item3: null, Item4: null, Item5: flange.SurfaceDesign.userData.isFlange });
            else
                this.linesToUpdate.Lines.push({ Item1: flange.Guid, Item2: null, Item3: null, Item4: null, Item5: flange.SurfaceDesign.userData.isFlange });
        }

        return fl
    }

    private computeOnFrontSide(n: THREE.Vector3) {
        for (const web of this.webs) {
            var normal = Util.LargestCanonicalAxisValue(n.clone());
            const sectionNormal = new THREE.Vector3(normal[0], normal[1], normal[2]);
            const l = []

            if (web.Line === null) {

                web.Line = [];

            }
            for (let i = 0; i < web.Line.length; i += 3) {
                const v = new Vector3(web.Line[i], web.Line[i + 1], web.Line[i + 2]);
                l.push(v);
            }
            if(web.Guid === 'd98881f6f40c46d2b8b3f16fb604b2df')
            {
                debugger
            }

            const line = !Util.isNullOrUndefined(web.SurfaceDesign.userData.newLine) ? web.SurfaceDesign.userData.newLine : l
            let found = false;
            for (const p of line) {
                const sideNormal = this.isOnfrontSide(web.SurfaceDesign, p, sectionNormal);
                if (!Util.isNullOrUndefined(sideNormal)) {
                    found = true;
                    if (web.SurfaceDesign.userData.sendLine)
                        this.linesToUpdate.Lines.push({
                            Item1: web.Guid, Item2: web.SurfaceDesign.userData.sendLine, Item3: sideNormal,
                            Item4: web.SurfaceDesign.userData.Flanges, Item5: false, Item6: web.SurfaceDesign.userData.IsOuter
                        });
                    break;
                }
            }
            if (!found) {
                const sideNormal = []
                sideNormal.push(sectionNormal.x);
                sideNormal.push(sectionNormal.y);
                sideNormal.push(sectionNormal.z);
                if (web.SurfaceDesign.userData.sendLine)
                    this.linesToUpdate.Lines.push({
                        Item1: web.Guid, Item2: web.SurfaceDesign.userData.sendLine,
                        Item3: sideNormal, Item4: web.SurfaceDesign.userData.Flanges, Item5: false, Item6: web.SurfaceDesign.userData.IsOuter
                    });

            }

        }
    }

    private isOnfrontSide(web, p, sectionNormal) {

        // const p: THREE.Vector3 = web.userData.newLine[1];
        let plane: THREE.Plane = null;
        let sideNormal = []
        if (Util.LargestCanonicalAxisDirection(sectionNormal) === 'x') {
            plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), p.y);
            let nP = this.checkTrianglesWithPlane(web, plane, p);
            if (Util.isNullOrUndefined(nP)) {
                nP = this.checkTrianglesWithPlane(web, new THREE.Plane(new THREE.Vector3(0, -1, 0), p.y), p);
            }
            if (nP && nP.x > p.x) {
                // web.material = this.graphics.greenMaterial
                sideNormal.push(1);
                sideNormal.push(0);
                sideNormal.push(0);
            }
            else if (nP && nP.x < p.x) {
                // web.material = this.graphics.redMaterial
                sideNormal.push(-1);
                sideNormal.push(0);
                sideNormal.push(0);
            }
        }
        else if (Util.LargestCanonicalAxisDirection(sectionNormal) === 'y') {
            plane = new THREE.Plane(new THREE.Vector3(1, 0, 0), p.x);
            let nP = this.checkTrianglesWithPlane(web, plane, p);
            if (Util.isNullOrUndefined(nP)) {
                nP = this.checkTrianglesWithPlane(web, new THREE.Plane(new THREE.Vector3(-1, 0, 0), p.x), p);
            }
            if (nP && nP.y > p.y) {
                // web.material = this.graphics.greenMaterial
                sideNormal.push(0);
                sideNormal.push(1);
                sideNormal.push(0);
            }
            else if (nP && nP.y < p.y) {
                // web.material = this.graphics.redMaterial
                sideNormal.push(0);
                sideNormal.push(-1);
                sideNormal.push(0);
            }
        }
        else {
            plane = new THREE.Plane(new THREE.Vector3(1, 0, 0), p.x);
            let nP = this.checkTrianglesWithPlane(web, plane, p);
            if (Util.isNullOrUndefined(nP)) {
                nP = this.checkTrianglesWithPlane(web, new THREE.Plane(new THREE.Vector3(-1, 0, 0), p.x), p);
            }
            if (nP && nP.z > p.z) {
                // web.material = this.graphics.greenMaterial
                sideNormal.push(0);
                sideNormal.push(0);
                sideNormal.push(1);
            }
            else if (nP && nP.y < p.y) {
                // web.material = this.graphics.redMaterial
                sideNormal.push(0);
                sideNormal.push(0);
                sideNormal.push(-1);
            }
        }

        return sideNormal;
    }

    private checkTrianglesWithPlane(web, plane: THREE.Plane, point: THREE.Vector3) {
        for (let i = 0; i < web.geometry.index.array.length; i += 3) {
            let pointOfIntersection = new THREE.Vector3();
            const vA = new THREE.Vector3(web.geometry.attributes.position.array[web.geometry.index.array[i] * 3],
                web.geometry.attributes.position.array[web.geometry.index.array[i] * 3 + 1],
                web.geometry.attributes.position.array[web.geometry.index.array[i] * 3 + 2]);

            const vB = new THREE.Vector3(web.geometry.attributes.position.array[web.geometry.index.array[i + 1] * 3],
                web.geometry.attributes.position.array[web.geometry.index.array[i + 1] * 3 + 1],
                web.geometry.attributes.position.array[web.geometry.index.array[i + 1] * 3 + 2]);

            const vC = new THREE.Vector3(web.geometry.attributes.position.array[web.geometry.index.array[i + 2] * 3],
                web.geometry.attributes.position.array[web.geometry.index.array[i + 2] * 3 + 1],
                web.geometry.attributes.position.array[web.geometry.index.array[i + 2] * 3 + 2]);

            const tri = new THREE.Triangle(vA, vB, vC);
            if (tri.getArea() === 0) {
                continue;
            }

            const lineAB = new THREE.Line3(vA, vB);
            const lineBC = new THREE.Line3(vB, vC);
            const lineCA = new THREE.Line3(vC, vA);

            pointOfIntersection = plane.intersectLine(lineAB, pointOfIntersection);
            if (pointOfIntersection && pointOfIntersection.distanceTo(point) > 0.05) {
                return pointOfIntersection;
            }
            pointOfIntersection = new THREE.Vector3();
            pointOfIntersection = plane.intersectLine(lineBC, pointOfIntersection);
            if (pointOfIntersection && pointOfIntersection.distanceTo(point) > 0.05) {
                return pointOfIntersection;
            }
            pointOfIntersection = new THREE.Vector3();
            pointOfIntersection = plane.intersectLine(lineCA, pointOfIntersection);
            if (pointOfIntersection && pointOfIntersection.distanceTo(point) > 0.05) {
                return pointOfIntersection;
            }
        }
    }

    private checkTriangleCollision(structure1: any, structure2: any, tolerance: number): [Array<THREE.Vector3>, Array<number>] {
        const line: Array<THREE.Vector3> = new Array<THREE.Vector3>();
        let lineIndexesList = [];
        // it's possible to have same point in different indexes of the position array,
        // need to delete duplicates
        // const filteredIndexesList = this.removeDuplicatedPoints(structure2.geometry.index.array, structure2.geometry.attributes.position.array);

        for (let i = 0; i < structure1.geometry.index.array.length; i += 3) {
            const vA = new THREE.Vector3(structure1.geometry.attributes.position.array[structure1.geometry.index.array[i] * 3],
                structure1.geometry.attributes.position.array[structure1.geometry.index.array[i] * 3 + 1],
                structure1.geometry.attributes.position.array[structure1.geometry.index.array[i] * 3 + 2]);

            const vB = new THREE.Vector3(structure1.geometry.attributes.position.array[structure1.geometry.index.array[i + 1] * 3],
                structure1.geometry.attributes.position.array[structure1.geometry.index.array[i + 1] * 3 + 1],
                structure1.geometry.attributes.position.array[structure1.geometry.index.array[i + 1] * 3 + 2]);

            const vC = new THREE.Vector3(structure1.geometry.attributes.position.array[structure1.geometry.index.array[i + 2] * 3],
                structure1.geometry.attributes.position.array[structure1.geometry.index.array[i + 2] * 3 + 1],
                structure1.geometry.attributes.position.array[structure1.geometry.index.array[i + 2] * 3 + 2]);

            const tri = new THREE.Triangle(vA, vB, vC);
            if (tri.getArea() === 0) {
                continue;
            }

            for (let j = 0; j < structure2.geometry.index.array.length; j++) {
                const jR = structure2.geometry.index.array[j];
                const vst = new THREE.Vector3(structure2.geometry.attributes.position.array[jR * 3],
                    structure2.geometry.attributes.position.array[jR * 3 + 1],
                    structure2.geometry.attributes.position.array[jR * 3 + 2]);
                let closestPoint = new THREE.Vector3();
                tri.closestPointToPoint(vst, closestPoint);
                if (closestPoint.distanceTo(vst) <= tolerance) {
                    line.push(vst);
                    lineIndexesList.push(jR);
                    // return true;
                }
            }
        }
        // return [line, lineIndexesList, filteredIndexesList];
        return [line, lineIndexesList];
    }

    private sortPointsToFormLineFacePlates(line: Array<THREE.Vector3>): Array<THREE.Vector3> {
        const lineBox = new THREE.Box3();
        line.forEach(l => {
            lineBox.expandByPoint(l);
        });

        const size = new THREE.Vector3();
        lineBox.getSize(size);

        if (size.x > size.y && size.x > size.z) {
            line.sort((a, b) => a.x > b.x && 1 || -1)
        }
        else if (size.y > size.x && size.y > size.z) {
            line.sort((a, b) => a.y > b.y && 1 || -1)
        }
        else {
            line.sort((a, b) => a.z > b.z && 1 || -1)
        }

        let current = line[0];
        line.splice(0, 1);
        const newLine = [current];
        while (line.length > 1) {
            current = line.reduce((a, b) => current.distanceTo(a) < current.distanceTo(b) ? a : b);
            let index = line.findIndex(x => x.equals(current));
            if (index > -1) {
                line.splice(index, 1);
                newLine.push(current);
            }
        }
        //
        newLine.push(line[0]);


        return newLine;
    }

    private sortPointsToFormLineFacePlates2(line: Array<THREE.Vector3>): Array<THREE.Vector3> {
        const lineBox = new THREE.Box3();
        line.forEach(l => {
            lineBox.expandByPoint(l);
        });

        const size = new THREE.Vector3();
        lineBox.getSize(size);

        if (size.x > size.y && size.x > size.z) {
            line.sort((a, b) => a.x > b.x && 1 || -1)
        }
        else if (size.y > size.x && size.y > size.z) {
            line.sort((a, b) => a.y > b.y && 1 || -1)
        }
        else {
            line.sort((a, b) => a.z > b.z && 1 || -1)
        }
        const lineScores = []
        let index = 0;
        for (const l of line) {
            const sorted = line.sort((a, b) => l.distanceTo(a) < l.distanceTo(b) && 1 || -1);

            lineScores.push({ index: index, score: l.distanceTo(sorted[0]) + l.distanceTo(sorted[1]) })
            // const b = line.reduce((a, b) => l.distanceTo(a) < l.distanceTo(b) ? a : b);

        }

        const max = lineScores.reduce((a, b) => a.score > b.score ? a : b);

        let current = line[max.index];
        line.splice(max.index, 1);
        const newLine = [current];
        while (line.length > 1) {
            current = line.reduce((a, b) => current.distanceTo(a) < current.distanceTo(b) ? a : b);
            let index = line.findIndex(x => x.equals(current));
            if (index > -1) {
                line.splice(index, 1);
                newLine.push(current);
            }
        }
        // newLine.push(line[0]);


        return newLine;
    }

    private boxesDistance(box1: THREE.Box3, box2: THREE.Box3): number {
        return Math.min(...[box1.distanceToPoint(new THREE.Vector3(box2.min.x, box2.max.y, box2.max.z)),
        box1.distanceToPoint(new THREE.Vector3(box2.max.x, box2.min.y, box2.min.z)),
        box1.distanceToPoint(new THREE.Vector3(box2.min.x, box2.max.y, box2.min.z)),
        box1.distanceToPoint(new THREE.Vector3(box2.min.x, box2.min.y, box2.max.z)),
        box1.distanceToPoint(new THREE.Vector3(box2.max.x, box2.min.y, box2.max.z)),
        box1.distanceToPoint(new THREE.Vector3(box2.max.x, box2.max.y, box2.min.z)),
        box1.distanceToPoint(box2.max), box1.distanceToPoint(box2.min)]);
    }

    private setRegionBoundingBoxAndCenter(region: any) {
        this.bBox.expandByObject(region.group);
        this.bBox.getCenter(this.regionCenterBox);
    }

    private setCurrentData(section: Section, resetData: boolean): void {
        if (resetData) {
            this.plates = [];
            this.stiffeners = [];
            this.facePlates = [];
            this.brackets = [];
            this.webs = [];
            this.flanges = [];
        }
        for (const structure of section.surfaceElements) {
            switch (structure.properties.TypeId) {
                case '51b97c79cf164388b36bf8edc326887c':
                    this.plates.push(structure);
                    break;
                default:
                    break;
            }
        }
        for (const structure of section.linearElements) {
            switch (structure.properties.TypeId) {
                case '8a124b71ba374e08aaded3e7073c8bf0':
                    if (structure.area > this.minimalStiffenerArea) {
                        structure.SurfaceDesign.userData.newLine = []
                        structure.SurfaceDesign.userData.newFlangeLine = []
                        this.stiffeners.push(structure);
                    }
                    break;
                case '573ef4493c3145128bb99d5daa131795':
                    this.facePlates.push(structure);
                    break;
                case 'a6ecb8f533f64e7aacb10122adef0d9b':
                    if (structure.area > this.minimalStiffenerArea) {
                        structure.SurfaceDesign.userData.newLine = []
                        structure.SurfaceDesign.userData.newFlangeLine = []
                        this.brackets.push(structure);
                    }
                    break;
                default:
                    break;
            }
        }

        const center = new THREE.Vector3();
        section.box3.getCenter(center);

        let raycaster = new THREE.Raycaster(center.clone(),
            section.sectionNormal.clone());
        let invertedRaycaster = new THREE.Raycaster(center.clone(),
            section.sectionNormal.clone().multiplyScalar(-1));

        let intersects = raycaster.intersectObjects(this.plates.map(x => x.SurfaceDesign));
        let intersects2 = invertedRaycaster.intersectObjects(this.plates.map(x => x.SurfaceDesign));

        let resultObject = intersects.length > 0 ? intersects[0] : intersects2.length > 0 ? intersects2[0] : null;
        if (resultObject) {
            let n = Util.LargestCanonicalAxisDirection(resultObject.face.normal);
            if (n === 'x')
                section.sectionNormal = new THREE.Vector3(1, 0, 0);
            else if (n === 'y')
                section.sectionNormal = new THREE.Vector3(0, 1, 0);
            else
                section.sectionNormal = new THREE.Vector3(0, 0, 1);
        }

        // for (const structure of section.surfaceElements) {
        //     structure.SurfaceDesign.visible = false;
        // }
    }

    private uniqueValues(array: Array<Vector3>): Array<Vector3> {
        const uniqueValuesArray = new Array<Vector3>();

        for (const value of array) {
            const index = uniqueValuesArray.findIndex(x => x.x === value.x && x.y === value.y && x.z === value.z)
            if (index === -1) {
                uniqueValuesArray.push(value);
            }
        }

        return uniqueValuesArray;
    }

    // temporary solution to notify user when utility ends running
    private waitUntilUtilityEndsExecution(regionsCount: number) {
        return new Promise<void>((res) => {
            setInterval(() => {
                let successCount = this.notificationService.notifications.filter(
                    n => (n.title === "Update Line Geometry" || n.title === "Add Reference Line Geometry") &&
                        n.message === "Success"
                ).length;
                // if (this.signalRService.notifications.length >= regionsCount * 4) {
                if (successCount >= regionsCount * 2) {
                    res()
                }
            }, 10000)
        })
    }

    private checkConectionLine(item, elements, addLine, fl):any {

        var centroidDistances: Array<number> = [];
        let itemBoxCenter: THREE.Vector3 = new THREE.Vector3();
        item.SurfaceDesign.geometry.computeBoundingBox();
        item.SurfaceDesign.geometry.boundingBox.getCenter(itemBoxCenter);
        for (const element of elements) {
            if (!element.SurfaceDesign.userData.isFlange) {
                let webBoxCenter: THREE.Vector3 = new THREE.Vector3();
                element.SurfaceDesign.geometry.computeBoundingBox();
                element.SurfaceDesign.geometry.boundingBox.getCenter(webBoxCenter);
                centroidDistances.push(webBoxCenter.distanceTo(itemBoxCenter));
            } else {
                centroidDistances.push(Number.MAX_SAFE_INTEGER);
            }
        }

        // assign web with the lowest distance to the flange centroid and intersects with it
        var localLine: Array<THREE.Vector3> = [];
        var index: number;
        do {

            index = centroidDistances.indexOf(Math.min(...centroidDistances));
            centroidDistances[index] = Number.MAX_SAFE_INTEGER;
            if((elements[index] as WorkableElement).Guid === 'd98881f6f40c46d2b8b3f16fb604b2df'){
                const asdasdff = 0
            }
            if(item.Guid === (elements[index] as WorkableElement).Guid )
                continue;
            if(elements[index].SurfaceDesign.userData.isFlange)
                continue;

            const direction = Util.LargestCanonicalAxisDirection((item as WorkableElement).SurfaceDesign.geometry.boundingBox.min.clone().
                sub((item as WorkableElement).SurfaceDesign.geometry.boundingBox.max.clone()));
            // const direction = Util.LargestCanonicalAxisDirection(localLine[0].clone().sub(localLine[localLine.length - 1].clone()));
            let pointDir = new THREE.Vector3();
            if (elements[index].SurfaceDesign.userData.newLine.length > 0)
                pointDir = elements[index].SurfaceDesign.userData.newLine[0].clone()
                    .sub(elements[index].SurfaceDesign.userData.newLine[elements[index].SurfaceDesign.userData.newLine.length - 1].clone());
            else
                pointDir = (elements[index] as WorkableElement).SurfaceDesign.geometry.boundingBox.min.clone().
                    sub((elements[index] as WorkableElement).SurfaceDesign.geometry.boundingBox.max.clone())
            const direction2 = Util.LargestCanonicalAxisDirection(pointDir);
            const sectionNormalDirection = Util.LargestCanonicalAxisDirection((item as WorkableElement).section.sectionNormal);

            if (direction !== sectionNormalDirection && direction2 !== sectionNormalDirection && direction !== direction2) {
                continue;
            }

            const result = this.checkTriangleCollision(elements[index].SurfaceDesign, item.SurfaceDesign, 0.15);
            localLine = result[0];
            if (localLine === undefined)
                continue
            localLine = this.uniqueValues(localLine);
            if (localLine.length == 1 || localLine.length === 0 || localLine[0] === undefined) {
                continue;
            }
            //stiffener.visible = false;
            localLine = this.sortPointsToFormLineFacePlates(localLine);
            if (localLine === undefined)
                continue

        } while ((localLine === undefined || localLine.length === 0) && Math.min(...centroidDistances) !== Number.MAX_SAFE_INTEGER);

        return {localLine:localLine, index:index};
    }

    private serializeGeometryForWorker(geometry: THREE.BufferGeometry) {
        if (!geometry) {
            return null;
        }

        return {
            boundingBox: this.serializeBoundingBox(geometry.boundingBox),
            attributes: this.serializeAttributesForWorker(geometry.attributes),
            index: this.serializeIndexForWorker(geometry.index),
        };
    }

    private serializeAttributesForWorker(attributes: any) {
        const positionArray = attributes?.position?.array;

        return {
            position: {
                array: positionArray,
            },
        };
    }

    private serializeIndexForWorker(index: THREE.BufferAttribute) {
        if (!index || !index.array) {
            return null;
        }

        return {
            array: index.array,
        };
    }

    private serializeBoundingBox(boundingBox: THREE.Box3) {
        if (!boundingBox) {
            return null;
        }

        return {
            min: {
                x: boundingBox.min.x,
                y: boundingBox.min.y,
                z: boundingBox.min.z,
            },
            max: {
                x: boundingBox.max.x,
                y: boundingBox.max.y,
                z: boundingBox.max.z,
            },
        };
    }
}

export class UpdateLineGeometryViewModel {
    public AssetId: string;
    public RegionId: string;
    public ChangeWebToFlange: boolean = false;
    public ChangeStiffenerToBracket: boolean = false;
    public Lines: Array<{ Item1: string, Item2: Array<number>, Item3: Array<number>, Item4: Array<string>, Item5: boolean, Item6?: boolean }>;
}

export class AddLineGeometryViewModel {
    public AssetId: string;
    public RegionId: string;
    public Lines: Array<{ Item1: string, Item2: string, Item3: string, Item4: Array<number> }>;
}
